/**
 * generateKolonSemaMultiMeter.js
 * Çok katlı ve çok sayaçlı yapılar için kolon şeması çizimi
 *
 * Koordinat Sistemi:
 * - Merkez Noktası: Notr barasının sol ucu (kırmızı daire ile işaretli)
 * - Enerji odası sol üst köşesi: Merkezden 100 birim sol, 200 birim yukarı
 *
 * Sütun Yapısı:
 * - Ana sütun: 1000 birim genişlik (sayaç ve kat panoları)
 * - Yardımcı sütunlar: 100 birim genişlik x 7 adet (müşterek, asansör, zayıf akım vb.)
 *
 * Kat Yükseklikleri:
 * - Enerji odası katı: 500 birim
 * - Diğer katlar: 150 birim
 */

import * as THREE from 'three';
import { KacakAkimTMS2, Boru, AnahtarliOtomatikSigorta, Sayac, KacakAkim, PanoTali, Lamba, InterkonButonGrup, Mak } from '../../modules/documents';
import { MCB, CABLES } from '../../electrical/Malzeme';

// Sabit değerler
const CONSTANTS = {
  CENTER_RADIUS: 15,            // Merkez noktası daire yarıçapı (çap 30 birim)
  CENTER_COLOR: 0xff0000,       // Merkez noktası rengi (kırmızı)

  MAIN_COLUMN_WIDTH: 1000,      // Ana sütun genişliği
  HELPER_COLUMN_WIDTH: 100,     // Yardımcı sütun genişliği
  HELPER_COLUMN_COUNT: 7,       // Yardımcı sütun sayısı

  ENERGY_ROOM_HEIGHT: 500,      // Enerji odası kat yüksekliği
  NORMAL_FLOOR_HEIGHT: 150,     // Normal kat yüksekliği

  // Enerji odası sol üst köşesinin merkeze göre offseti
  ENERGY_ROOM_OFFSET_X: -100,   // Merkeze göre sol
  ENERGY_ROOM_OFFSET_Y: 275,    // Merkeze göre yukarı

  LINE_COLOR: 0x333333,         // Çizgi rengi
  FLOOR_LINE_COLOR: 0x0066cc,   // Kat çizgisi rengi
  COLUMN_LINE_COLOR: 0x666666,  // Sütun çizgisi rengi

  // Bara sabitleri
  BAR_SPACING: 15,              // Baralar arası mesafe
  BAR_BASE_WIDTH: 700,          // İlk 6 sayaç için sabit bara genişliği
  BAR_PER_METER_EXTRA: 100,     // 6 sayaçtan sonra her sayaç için ek genişlik
  BAR_BASE_METER_COUNT: 6,      // Sabit genişlik için sayaç limiti
  BAR_LABEL_FONT_SIZE: 9,       // Bara etiketi punto
  BAR_LABEL_MARGIN: 10,         // Etiket ile bara ucu arası mesafe

  // Bağlantı noktası sabitleri
  CONNECTION_DOT_RADIUS: 3,     // Bağlantı noktası dolu daire yarıçapı
  CONNECTION_DOT_COLOR: 0x000000, // Bağlantı noktası rengi (siyah - varsayılan)

  // Eş potansiyel bara sabitleri
  ES_POTANSIYEL_INDENT: 15,     // Pe barasının sol ucundan sağa girinti
  ES_POTANSIYEL_LENGTH: 165,    // Aşağı doğru bağlantı çizgisi uzunluğu

  // Bara renkleri
  BAR_COLOR_R: 0x8B4513,        // R fazı - kahverengi
  BAR_COLOR_S: 0xffffff,        // S fazı - beyaz (siyah sahnede görünürlük için)
  BAR_COLOR_T: 0x888888,        // T fazı - gri
  BAR_COLOR_NOTR: 0x0000ff,     // Notr - mavi
  BAR_COLOR_PE: 0x00ff00,       // Pe (topraklama) - yeşil
};

/**
 * Merkez noktası işaretçisi oluştur (kırmızı daire)
 */
const createCenterMarker = (centerX, centerY) => {
  const geometry = new THREE.CircleGeometry(CONSTANTS.CENTER_RADIUS, 32);
  const material = new THREE.MeshBasicMaterial({
    color: CONSTANTS.CENTER_COLOR,
    transparent: true,
    opacity: 0.5
  });
  const circle = new THREE.Mesh(geometry, material);
  circle.position.set(centerX, centerY, 0);
  circle.userData = {
    type: 'circle',
    role: 'centerMarker',
    isKolonSema: true,
    center: { x: 0, y: 0 },
    radius: CONSTANTS.CENTER_RADIUS
  };
  return circle;
};

/**
 * Çizgi oluştur
 */
const createLine = (x1, y1, x2, y2, color = CONSTANTS.LINE_COLOR, userData = {}) => {
  const points = [
    new THREE.Vector3(x1, y1, 0),
    new THREE.Vector3(x2, y2, 0)
  ];
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const material = new THREE.LineBasicMaterial({ color });
  const line = new THREE.Line(geometry, material);
  line.userData = {
    isKolonSema: true,
    ...userData
  };
  return line;
};

/**
 * Kat çizgilerini oluştur
 * Koordinat sistemi: Y yukarı doğru artar
 * - Normal katlar: Yukarı doğru (Y artar)
 * - Bodrum katlar: Aşağı doğru (Y azalır)
 */
const createFloorLines = (config, centerX, centerY) => {
  const lines = [];
  const { tumKatlar, enerjiOdasiKati } = config;

  // Jeneratör var mı? (sütun yapısını belirler)
  const jeneratorVar = config.gtConfig && Object.values(config.gtConfig).some(kc => kc.jenerator);

  // Tali panel var mı? (sütun yapısını belirler)
  const hasTaliPaneller = (config.gtTaliPaneller && config.gtTaliPaneller.length > 0)
    || (config.gtConfig && Object.values(config.gtConfig).some(kc =>
      ['asansor','kazanDairesi','siginak','otopark','mustemilat','yanginIhbar'].some(k => kc[k])
    ));

  // Sayaç sayısına göre ana sütun genişliği
  const panelsPerFloor = config.panelsPerFloor || {};
  const hasPanelsPerFloor = config.hasPanelData || Object.keys(panelsPerFloor).length > 0;
  const toplamKat = (parseInt(config.bodrumKat) || 0)
    + 1
    + (config.asmaKat ? 1 : 0)
    + (config.galeriKat ? 1 : 0)
    + (parseInt(config.normalKat) || 0)
    + (config.catiKat ? 1 : 0);
  const katBasiDaire = parseInt(config.katBasiDaire) || 1;
  const sayacAdet = hasPanelsPerFloor
    ? tumKatlar.reduce((sum, kat) => sum + (panelsPerFloor[kat] || 0), 0)
    : toplamKat * katBasiDaire;
  const mainColumnWidth = (sayacAdet > 6
    ? CONSTANTS.MAIN_COLUMN_WIDTH + (sayacAdet - 6) * 100
    : CONSTANTS.MAIN_COLUMN_WIDTH) - 10;

  // Yardımcı sütun genişlikleri: [Tali Panolar], [Jeneratör], Merdiven, Asansör, Zayıf Akım
  const helperColumnWidths = [
    ...(hasTaliPaneller ? [310] : []),
    ...(jeneratorVar ? [100] : []),
    100, 100, 100,  // Merdiven, Asansör, ZA
  ];
  const helperTotalWidth = helperColumnWidths.reduce((a, b) => a + b, 0);

  // Yardımcı sütun sol kenar X pozisyonları (mainColumnWidth'den sonra)
  const helperColumnX = [];
  let hx = 0;
  helperColumnWidths.forEach(w => {
    helperColumnX.push(hx);
    hx += w;
  });

  // Toplam genişlik: Ana sütun + Yardımcı sütunlar
  const totalWidth = mainColumnWidth + helperTotalWidth;

  // Enerji odası sol üst köşesi
  const energyRoomLeft = centerX + CONSTANTS.ENERGY_ROOM_OFFSET_X;
  const energyRoomTop = centerY + CONSTANTS.ENERGY_ROOM_OFFSET_Y;

  // Enerji odası katının index'ini bul
  const enerjiKatIndex = tumKatlar.indexOf(enerjiOdasiKati);

  // Her kat için y pozisyonunu hesapla
  const katYPositions = {};

  // Enerji odası katında panel var mı? Varsa yüksekliği bir kat boyu artır
  const enerjiOdasiPanelVar = (panelsPerFloor[enerjiOdasiKati] || 0) > 0;
  const extraEnerjiHeight = enerjiOdasiPanelVar ? CONSTANTS.NORMAL_FLOOR_HEIGHT : 0;
  const enerjiHeight = CONSTANTS.ENERGY_ROOM_HEIGHT + extraEnerjiHeight;
  const energyRoomTopAdjusted = energyRoomTop + extraEnerjiHeight;
  katYPositions[enerjiOdasiKati] = {
    top: energyRoomTopAdjusted,
    bottom: energyRoomTop - CONSTANTS.ENERGY_ROOM_HEIGHT,
    height: enerjiHeight,
    isEnergyRoom: true
  };

  // Enerji odası katından YUKARI doğru (üst katlar - index artar)
  let currentY = energyRoomTopAdjusted;
  for (let i = enerjiKatIndex + 1; i < tumKatlar.length; i++) {
    const kat = tumKatlar[i];
    const height = CONSTANTS.NORMAL_FLOOR_HEIGHT;

    // Bir üst kata çık (Y artar)
    currentY = currentY + height;
    katYPositions[kat] = {
      top: currentY,
      bottom: currentY - height,
      height: height,
      isEnergyRoom: false
    };
  }

  // Enerji odası katından AŞAĞI doğru (alt katlar - index azalır)
  currentY = energyRoomTop - enerjiHeight; // Enerji odasının altından başla
  for (let i = enerjiKatIndex - 1; i >= 0; i--) {
    const kat = tumKatlar[i];
    const height = CONSTANTS.NORMAL_FLOOR_HEIGHT;

    // Bir alt kata in (Y azalır)
    katYPositions[kat] = {
      top: currentY,
      bottom: currentY - height,
      height: height,
      isEnergyRoom: false
    };
    currentY = currentY - height;
  }

  // Şemanın toplam yükseklik sınırlarını bul
  let minY = Infinity, maxY = -Infinity;
  Object.values(katYPositions).forEach(pos => {
    minY = Math.min(minY, pos.bottom);
    maxY = Math.max(maxY, pos.top);
  });

  // Topraklama (E.P.B. + kazık) bölgesi çerçevenin altında kalabiliyor —
  // dikey kenarları ve alt kapanış çizgisini Topraklama'yı da kapsayacak
  // şekilde uzat. Formüller createEsPotansiyelBara ile aynı.
  const peBarY = centerY - 2 * CONSTANTS.BAR_SPACING;
  const esPotBottomY = peBarY - CONSTANTS.ES_POTANSIYEL_LENGTH;
  // toprakDownY = bottomY - 80, kazık alt = -20, text padding = ~10 → 110
  const topraklamaBottomY = esPotBottomY - 110;
  const frameMinY = Math.min(minY, topraklamaBottomY);

  // Yatay kat çizgileri oluştur
  // frameMinY'yi şimdilik minY olarak başlat; Topraklama hesabı aşağıda yapılıyor
  // ve eğer altına taşıyorsa lowest floor'un alt kapanış çizgisi gereksizleşiyor
  // (frameBottom yatay çizgisi onun yerine kullanılıyor).
  tumKatlar.forEach((kat, index) => {
    const pos = katYPositions[kat];
    if (!pos) return;

    // Üst çizgi
    lines.push(createLine(
      energyRoomLeft, pos.top,
      energyRoomLeft + totalWidth, pos.top,
      CONSTANTS.FLOOR_LINE_COLOR,
      { role: 'floorLine', floor: kat, position: 'top' }
    ));

    // Alt çizgi (sadece en alt kat için) — Topraklama aşağı taşmıyorsa frame
    // kapanışı için gerekli. Taşıyorsa frameBottom çizgisi zaten ekleniyor.
    if (index === 0 && topraklamaBottomY >= minY) {
      lines.push(createLine(
        energyRoomLeft, pos.bottom,
        energyRoomLeft + totalWidth, pos.bottom,
        CONSTANTS.FLOOR_LINE_COLOR,
        { role: 'floorLine', floor: kat, position: 'bottom' }
      ));
    }
  });

  // --- Başlık satırı (en üstte, 40 birim yükseklik) ---
  const headerHeight = 40;
  const headerTop = maxY + headerHeight;
  const headerMidY = maxY + headerHeight / 2;

  // Başlık üst yatay çizgi
  lines.push(createLine(
    energyRoomLeft, headerTop,
    energyRoomLeft + totalWidth, headerTop,
    CONSTANTS.FLOOR_LINE_COLOR,
    { role: 'headerLine', position: 'top' }
  ));

  // Başlık alt yatay çizgi (maxY zaten en üst kat çizgisi)

  // Başlık metinleri
  const headerLabels = [];
  headerLabels.push({ text: 'Kat Panoları', col: 0 });
  let nextCol = 1;
  if (hasTaliPaneller) {
    headerLabels.push({ text: 'Tali Panolar', col: nextCol });
    nextCol++;
  }
  if (jeneratorVar) {
    headerLabels.push({ text: 'Jeneratör', col: nextCol });
    nextCol++;
  }
  headerLabels.push({ text: 'Merdiven', text2: 'Aydınlatma', col: nextCol });
  nextCol++;
  headerLabels.push({ text: 'Asansör', text2: 'Aydınlatma', col: nextCol });
  nextCol++;
  headerLabels.push({ text: 'Zayıf Akım', col: nextCol });

  headerLabels.forEach(h => {
    let centerX;
    if (h.col === 0) {
      // Ana sütun ortası
      centerX = energyRoomLeft + mainColumnWidth / 2;
    } else {
      // Yardımcı sütunlar: helperColumnX ve helperColumnWidths kullan
      const colIdx = h.col - 1;
      const colLeft = energyRoomLeft + mainColumnWidth + helperColumnX[colIdx];
      centerX = colLeft + helperColumnWidths[colIdx] / 2;
    }
    if (h.text2) {
      // İki satır: üst ve alt
      lines.push(createTextSprite(h.text, centerX, headerMidY + 8, 16, '#ffffff'));
      lines.push(createTextSprite(h.text2, centerX, headerMidY - 8, 16, '#ffffff'));
    } else {
      lines.push(createTextSprite(h.text, centerX, headerMidY, 20, '#ffffff'));
    }
  });

  // Dikey sütun çizgileri oluştur — hepsi frameMinY'ye kadar uzar
  // (Topraklama aşağı taşıyorsa tümü onunla aynı seviyede biter).
  lines.push(createLine(
    energyRoomLeft, headerTop,
    energyRoomLeft, frameMinY,
    CONSTANTS.FLOOR_LINE_COLOR,
    { role: 'columnLine', column: 'mainLeft' }
  ));

  lines.push(createLine(
    energyRoomLeft + mainColumnWidth, headerTop,
    energyRoomLeft + mainColumnWidth, frameMinY,
    CONSTANTS.FLOOR_LINE_COLOR,
    { role: 'columnLine', column: 'mainRight' }
  ));

  const skipFirst = hasTaliPaneller ? 1 : 0;
  for (let i = skipFirst; i < helperColumnWidths.length; i++) {
    const x = energyRoomLeft + mainColumnWidth + helperColumnX[i];
    lines.push(createLine(
      x, headerTop,
      x, frameMinY,
      CONSTANTS.FLOOR_LINE_COLOR,
      { role: 'columnLine', column: `helper${i}` }
    ));
  }

  lines.push(createLine(
    energyRoomLeft + totalWidth, headerTop,
    energyRoomLeft + totalWidth, frameMinY,
    CONSTANTS.FLOOR_LINE_COLOR,
    { role: 'columnLine', column: 'rightEdge' }
  ));

  // Topraklama kat grid'inin altında ise tek bir alt kapanış yatay çizgisi
  // (sol kenardan sağ kenara, frameMinY hizasında).
  if (frameMinY < minY - 0.5) {
    lines.push(createLine(
      energyRoomLeft, frameMinY,
      energyRoomLeft + totalWidth, frameMinY,
      CONSTANTS.FLOOR_LINE_COLOR,
      { role: 'floorLine', position: 'frameBottom' }
    ));
  }

  return { lines, katYPositions, bounds: { minY: frameMinY, maxY: headerTop, left: energyRoomLeft, right: energyRoomLeft + totalWidth } };
};

/**
 * Canvas tabanlı text sprite oluştur
 */
const createTextSprite = (text, x, y, fontSize = CONSTANTS.BAR_LABEL_FONT_SIZE, color = '#000000', align = 'center') => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const scale = 4; // Çözünürlük çarpanı
  const pxSize = fontSize * scale;
  ctx.font = `bold ${pxSize}px Arial`;
  const metrics = ctx.measureText(text);
  const textWidth = metrics.width;
  const textHeight = pxSize;

  canvas.width = textWidth + 4 * scale;
  canvas.height = textHeight + 4 * scale;

  // Font'u tekrar ayarla (canvas boyutu değişince sıfırlanıyor)
  ctx.font = `bold ${pxSize}px Arial`;
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.textAlign = align === 'left' ? 'left' : 'center';
  ctx.fillText(text, align === 'left' ? 2 * scale : canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;

  const spriteMaterial = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
  });
  const sprite = new THREE.Sprite(spriteMaterial);

  // Sprite boyutunu birim cinsine dönüştür (canvas piksel -> dünya birimi)
  const worldWidth = canvas.width / scale;
  const worldHeight = canvas.height / scale;
  sprite.scale.set(worldWidth, worldHeight, 1);

  // Sol hizalama: sprite'ın sol kenarı x'e gelsin (sprite merkezi x + worldWidth/2)
  if (align === 'left') {
    sprite.position.set(x + worldWidth / 2, y, 0.1);
  } else {
    sprite.position.set(x, y, 0.1);
  }

  sprite.userData = {
    type: 'text',
    role: 'barLabel',
    isKolonSema: true,
    text: text,
    fontSize: fontSize,
    labelText: text
  };

  return sprite;
};

/**
 * Baraları oluştur (R, S, T, Notr, Pe)
 * Notr barası merkez noktasında, üstünde faz baraları, altında Pe
 */
const createBars = (centerX, centerY, totalMeterCount) => {
  const objects = [];

  // Bara genişliği: 6 sayaca kadar 700, sonrası her sayaç +100
  const barWidth = totalMeterCount > 6
    ? CONSTANTS.BAR_BASE_WIDTH + (totalMeterCount - 6) * 100
    : CONSTANTS.BAR_BASE_WIDTH;

  // Bara tanımları: yukarıdan aşağı R, S, T, Notr, Pe
  // Notr merkez noktasında (centerY), diğerleri spacing kadar yukarı/aşağı
  const bars = [
    { name: 'R',    color: CONSTANTS.BAR_COLOR_R,    offset:  2 * CONSTANTS.BAR_SPACING },
    { name: 'S',    color: CONSTANTS.BAR_COLOR_S,    offset:  1 * CONSTANTS.BAR_SPACING },
    { name: 'T',    color: CONSTANTS.BAR_COLOR_T,    offset:  0 },
    { name: 'Notr', color: CONSTANTS.BAR_COLOR_NOTR, offset: -1 * CONSTANTS.BAR_SPACING },
    { name: 'Pe',   color: CONSTANTS.BAR_COLOR_PE,   offset: -2 * CONSTANTS.BAR_SPACING },
  ];

  const barStartX = centerX;
  const barEndX = centerX + barWidth;

  bars.forEach(bar => {
    const barY = centerY + bar.offset;

    // Bara çizgisi
    objects.push(createLine(
      barStartX, barY,
      barEndX, barY,
      bar.color,
      { role: 'bar', barName: bar.name }
    ));

    // Sol etiket
    objects.push(createTextSprite(
      bar.name,
      barStartX - CONSTANTS.BAR_LABEL_MARGIN - (bar.name.length * 3),
      barY,
      CONSTANTS.BAR_LABEL_FONT_SIZE,
      '#' + bar.color.toString(16).padStart(6, '0')
    ));

    // Sağ etiket
    objects.push(createTextSprite(
      bar.name,
      barEndX + CONSTANTS.BAR_LABEL_MARGIN + (bar.name.length * 3),
      barY,
      CONSTANTS.BAR_LABEL_FONT_SIZE,
      '#' + bar.color.toString(16).padStart(6, '0')
    ));
  });

  return { objects, barWidth, barStartX, barEndX };
};

/**
 * Bağlantı noktası (dolu daire) oluştur
 */
const createConnectionDot = (x, y, color = CONSTANTS.CONNECTION_DOT_COLOR) => {
  const geometry = new THREE.CircleGeometry(CONSTANTS.CONNECTION_DOT_RADIUS, 16);
  const material = new THREE.MeshBasicMaterial({ color });
  const circle = new THREE.Mesh(geometry, material);
  circle.position.set(x, y, 0.05);

  // Solid hatch boundary: daire şeklinde nokta dizisi (local koordinat)
  const r = CONSTANTS.CONNECTION_DOT_RADIUS;
  const segments = 16;
  const hatchPoints = [];
  for (let i = 0; i < segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    hatchPoints.push({ x: Math.cos(angle) * r, y: Math.sin(angle) * r, z: 0 });
  }

  circle.userData = {
    type: 'solidHatch',
    role: 'connectionDot',
    isKolonSema: true,
    points: hatchPoints
  };
  return circle;
};

/**
 * İçi açık (boş) daire oluştur
 */
const createHollowCircle = (x, y, radius = CONSTANTS.CONNECTION_DOT_RADIUS, color = CONSTANTS.CONNECTION_DOT_COLOR) => {
  const segments = 32;
  const points = [];
  for (let i = 0; i <= segments; i++) {
    const angle = (i / segments) * Math.PI * 2;
    points.push(new THREE.Vector3(
      x + Math.cos(angle) * radius,
      y + Math.sin(angle) * radius,
      0.05
    ));
  }
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const material = new THREE.LineBasicMaterial({ color });
  const circle = new THREE.Line(geometry, material);
  circle.userData = {
    role: 'hollowCircle',
    isKolonSema: true
  };
  return circle;
};

/**
 * Dörtgen (rectangle) oluştur - sadece kenar çizgileri
 */
const createRectangle = (x, y, width, height, color = CONSTANTS.LINE_COLOR) => {
  const points = [
    new THREE.Vector3(x, y, 0.05),
    new THREE.Vector3(x + width, y, 0.05),
    new THREE.Vector3(x + width, y - height, 0.05),
    new THREE.Vector3(x, y - height, 0.05),
    new THREE.Vector3(x, y, 0.05), // kapatma
  ];
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const material = new THREE.LineBasicMaterial({ color });
  const rect = new THREE.Line(geometry, material);
  rect.userData = {
    role: 'rectangle',
    isKolonSema: true
  };
  return rect;
};

/**
 * Eş potansiyel bara oluştur
 * Pe barasının sol ucundan 15 birim sağda bağlantı noktası,
 * oradan aşağı doğru 165 birim dikey çizgi
 *
 * Dinamik elemanlar (kolonConfig'den):
 * - topraklamaMetalSu: Metal Su Boruları
 * - topraklamaMetalGaz: Metal Gaz Boruları
 * - topraklamaAsansorRay: Asansör Rayları
 * - topraklamaKabloMerdiven: Kablo Merdiveni
 */
const createEsPotansiyelBara = (centerX, centerY, kolonConfig) => {
  const objects = [];

  // Seçili elemanları topla (sıralı: topraklama kazığı her zaman ilk dairede)
  const epbItems = [];
  if (kolonConfig.topraklamaMetalSu) epbItems.push({ key: 'metalSu', label: 'Metal Su Boruları' });
  if (kolonConfig.topraklamaMetalGaz) epbItems.push({ key: 'metalGaz', label: 'Metal Gaz Boruları' });
  if (kolonConfig.topraklamaAsansorRay) epbItems.push({ key: 'asansorRay', label: 'Asansör Rayları' });
  if (kolonConfig.topraklamaKabloMerdiven) epbItems.push({ key: 'kabloMerdiven', label: 'Kablo Merdiveni' });

  // Toplam daire sayısı: 1 (topraklama kazığı) + seçili eleman sayısı
  const circleCount = 1 + epbItems.length; // topraklama + seçilenler

  // Pe barasının Y pozisyonu (merkez noktadan 2 * spacing aşağıda)
  const peBarY = centerY - 2 * CONSTANTS.BAR_SPACING;

  // Bağlantı noktası X pozisyonu: Pe bara sol ucu (centerX) + 15 birim girinti
  const connectionX = centerX + CONSTANTS.ES_POTANSIYEL_INDENT;

  // 1. Bağlantı noktası (dolu daire) - Pe barası üzerinde
  objects.push(createConnectionDot(connectionX, peBarY, CONSTANTS.BAR_COLOR_PE));

  // 2. Dikey çizgi: bağlantı noktasından aşağı doğru 165 birim
  const bottomY = peBarY - CONSTANTS.ES_POTANSIYEL_LENGTH;
  objects.push(createLine(
    connectionX, peBarY,
    connectionX, bottomY,
    CONSTANTS.BAR_COLOR_PE,
    { role: 'esPotansiyelLine' }
  ));

  // 3. Alt uçta daireler (r=3, merkezler arası 20 birim)
  const circleSpacing = 20;
  const r = CONSTANTS.CONNECTION_DOT_RADIUS;
  const padding = 2;

  for (let i = 0; i < circleCount; i++) {
    const cx = connectionX + i * circleSpacing;
    objects.push(createHollowCircle(cx, bottomY, r, CONSTANTS.BAR_COLOR_PE));
  }

  // 4. Daireları çevreleyen dörtgen
  const rectLeft = connectionX - r - padding;
  const rectTop = bottomY + r + padding;
  const rectRight = connectionX + (circleCount - 1) * circleSpacing + r + padding;
  const rectBottom = bottomY - r - padding;
  const rectWidth = rectRight - rectLeft;
  const rectHeight = rectTop - rectBottom;

  objects.push(createRectangle(rectLeft, rectTop, rectWidth, rectHeight, CONSTANTS.BAR_COLOR_PE));

  // 5. "E.P.B." yazısı (dörtgenin solunda, 10 birim yukarı 25 birim sola kaydırılmış)
  const epbFontSize = 12;
  objects.push(createTextSprite(
    'E.P.B.',
    rectLeft - 45,
    bottomY + 10,
    epbFontSize,
    '#00ff00'
  ));

  // Daire merkez X pozisyonları
  const circleX = (i) => connectionX + i * circleSpacing;
  const labelFontSize = 12;

  // 6. İlk daire (index 0): Topraklama kazığı (50 birim sola kaydırılmış)
  const toprakX = circleX(0);
  const kazikX = toprakX - 50; // Kazık 50 birim solda
  const toprakDownY = bottomY - 80;

  // Yatay çizgi: kazık dikey çizgisinin üst ucundan sağa doğru E.P.B. dairesine
  objects.push(createLine(
    kazikX, bottomY,
    toprakX, bottomY,
    CONSTANTS.BAR_COLOR_PE,
    { role: 'topraklamaYatayBaglanti' }
  ));

  // Dikey çizgi: kazığa inen hat (50 birim solda)
  objects.push(createLine(
    kazikX, bottomY,
    kazikX, toprakDownY,
    CONSTANTS.BAR_COLOR_PE,
    { role: 'esPotansiyelBranch' }
  ));

  const kazikWidths = [40, 32, 24, 16, 8];
  const kazikSpacing = 5;
  kazikWidths.forEach((w, i) => {
    const ky = toprakDownY - (i * kazikSpacing);
    objects.push(createLine(
      kazikX - w / 2, ky,
      kazikX + w / 2, ky,
      CONSTANTS.BAR_COLOR_PE,
      { role: 'topraklamaKazik' }
    ));
  });

  objects.push(createTextSprite(
    // Sağa kaydırıldı: eskiden kazikX-45'te metin çerçevenin soluna taşıyor, bbox'ı
    // sola genişletip buildKolonSemaMultiMeter merkezlemesinde Kolon'u sağa kaydırıyordu
    // (diğer dokümanlara göre girintili). Metni çerçeve içine al.
    'Topraklama',
    kazikX - 30,
    toprakDownY - ((kazikWidths.length - 1) * kazikSpacing) / 2 + 15,
    labelFontSize,
    '#00ff00'
  ));

  // 8. Seçili elemanlar (index 1'den başlayarak sağa doğru)
  epbItems.forEach((item, idx) => {
    const itemCircleIdx = idx + 1; // 0 = topraklama, 1+ = seçili elemanlar
    const itemX = circleX(itemCircleIdx);
    const downDistance = 25 + (epbItems.length - 1 - idx) * 25;
    const itemDownY = bottomY - downDistance;
    const rightDistance = 145;

    // Dikey çizgi (daireden aşağı)
    objects.push(createLine(
      itemX, bottomY,
      itemX, itemDownY,
      CONSTANTS.BAR_COLOR_PE,
      { role: 'esPotansiyelBranch' }
    ));

    // Yatay çizgi (sağa doğru)
    objects.push(createLine(
      itemX, itemDownY,
      itemX + rightDistance, itemDownY,
      CONSTANTS.BAR_COLOR_PE,
      { role: 'esPotansiyelBranch' }
    ));

    // Etiket (çizgi üstünde)
    objects.push(createTextSprite(
      item.label,
      itemX + rightDistance / 2, itemDownY + 7,
      labelFontSize,
      '#00ff00'
    ));
  });

  return objects;
};

/**
 * Kesikli çizgi oluştur
 */
const createDashedLine = (x1, y1, x2, y2, color = CONSTANTS.LINE_COLOR, userData = {}, dashSize = 6, gapSize = 4) => {
  const points = [
    new THREE.Vector3(x1, y1, 0),
    new THREE.Vector3(x2, y2, 0)
  ];
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  geometry.computeBoundingSphere();
  const material = new THREE.LineDashedMaterial({ color, dashSize, gapSize });
  const line = new THREE.Line(geometry, material);
  line.computeLineDistances();
  line.userData = {
    isKolonSema: true,
    ...userData
  };
  return line;
};

/**
 * DXF entity'lerini Three.js objelerine dönüştür
 * views.js'deki tms gibi fonksiyonların çıktısını sahneye eklenebilir hale getirir
 * @param {boolean} dashed - Kesikli çizgi olarak çiz
 */
const convertDxfToThreeJS = (dxfEntities, color = 0x000000, dashed = false) => {
  const objects = [];

  const makeMaterial = () => {
    if (dashed) {
      return new THREE.LineDashedMaterial({ color, dashSize: 6, gapSize: 4 });
    }
    return new THREE.LineBasicMaterial({ color });
  };

  const makeLine = (geometry, role) => {
    const material = makeMaterial();
    const line = new THREE.Line(geometry, material);
    if (dashed) line.computeLineDistances();
    line.userData = { isKolonSema: true, role };
    return line;
  };

  dxfEntities.forEach(entity => {
    switch (entity.type) {
      case 'LINE': {
        if (entity.vertices && entity.vertices.length >= 2) {
          const points = entity.vertices.map(v => new THREE.Vector3(v.x, v.y, v.z || 0));
          const geometry = new THREE.BufferGeometry().setFromPoints(points);
          objects.push(makeLine(geometry, 'dxfLine'));
        }
        break;
      }
      case 'LWPOLYLINE': {
        if (entity.vertices && entity.vertices.length >= 2) {
          const points = entity.vertices.map(v => new THREE.Vector3(v.x, v.y, v.z || 0));
          // shape (kapalı) ise son noktayı ekle
          if (entity.shape && points.length > 0) {
            const first = points[0];
            const last = points[points.length - 1];
            if (first.x !== last.x || first.y !== last.y) {
              points.push(first.clone());
            }
          }
          const geometry = new THREE.BufferGeometry().setFromPoints(points);
          objects.push(makeLine(geometry, 'dxfPolyline'));
        }
        break;
      }
      case 'CIRCLE': {
        if (entity.center && entity.radius) {
          const segments = 32;
          const circlePoints = [];
          for (let i = 0; i <= segments; i++) {
            const angle = (i / segments) * Math.PI * 2;
            circlePoints.push(new THREE.Vector3(
              entity.center.x + Math.cos(angle) * entity.radius,
              entity.center.y + Math.sin(angle) * entity.radius,
              entity.center.z || 0
            ));
          }
          const geometry = new THREE.BufferGeometry().setFromPoints(circlePoints);
          objects.push(makeLine(geometry, 'dxfCircle'));
        }
        break;
      }
      case 'ARC': {
        if (entity.center && entity.radius) {
          const segments = 32;
          const arcPoints = [];
          const startAngle = entity.startAngle || 0;
          const endAngle = entity.endAngle || Math.PI * 2;
          for (let i = 0; i <= segments; i++) {
            const angle = startAngle + (i / segments) * (endAngle - startAngle);
            arcPoints.push(new THREE.Vector3(
              entity.center.x + Math.cos(angle) * entity.radius,
              entity.center.y + Math.sin(angle) * entity.radius,
              entity.center.z || 0
            ));
          }
          const geometry = new THREE.BufferGeometry().setFromPoints(arcPoints);
          objects.push(makeLine(geometry, 'dxfArc'));
        }
        break;
      }
      case 'TEXT': {
        const tx = entity.startPoint?.x ?? entity.x;
        const ty = entity.startPoint?.y ?? entity.y;
        if (tx != null && entity.text) {
          const textColor = '#' + color.toString(16).padStart(6, '0');
          const fontSize = entity.textHeight || 12;
          const sprite = createTextSprite(
            entity.text,
            tx, ty,
            fontSize, textColor
          );
          // Rotation uygula (derece → radyan)
          if (entity.rotation) {
            sprite.material.rotation = (entity.rotation * Math.PI) / 180;
          }
          objects.push(sprite);
        }
        break;
      }
      case 'HATCH': {
        // Solid hatch: boundary path'lerden filled mesh oluştur
        const hx = entity.x || 0;
        const hy = entity.y || 0;
        if (entity.boundaryPath) {
          entity.boundaryPath.forEach(bp => {
            if (bp.groups) {
              bp.groups.forEach(group => {
                if (group.points && group.points.length >= 6) {
                  const pts = group.points;
                  const shape = new THREE.Shape();
                  // İlk nokta (local koordinat + merkez offset)
                  shape.moveTo(hx + pts[0], hy + pts[1]);
                  for (let i = 2; i < pts.length; i += 2) {
                    shape.lineTo(hx + pts[i], hy + pts[i + 1]);
                  }
                  shape.closePath();
                  const geometry = new THREE.ShapeGeometry(shape);
                  const material = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });
                  const mesh = new THREE.Mesh(geometry, material);
                  mesh.position.z = 0.02;
                  mesh.userData = { isKolonSema: true, role: 'dxfHatch' };
                  objects.push(mesh);
                }
              });
            }
          });
        }
        break;
      }
      default:
        break;
    }
  });

  return objects;
};

/**
 * Ana Kolon Hattı oluştur
 * R fazından başlayıp aşağı+sağa inen kırmızı hat
 * Hattın ucuna TMS şalter yerleştirilir
 */
const createAnaKolonHatti = (centerX, centerY, kolonConfig, tumKatlar, katYPositions, barWidth) => {
  const objects = [];
  const anaKolonColor = 0xff0000; // Kırmızı
  const tmsSimetrik = kolonConfig.tmsSimetrik || false;
  const anaKolonFazi = kolonConfig.anaKolonFazi || 'R';

  // Faz Y pozisyonları
  const fazYPozisyonlari = {
    R:    centerY + 2 * CONSTANTS.BAR_SPACING,
    S:    centerY + 1 * CONSTANTS.BAR_SPACING,
    T:    centerY,
    Notr: centerY - 1 * CONSTANTS.BAR_SPACING,
  };

  // Başlangıç fazını belirle (RST seçildiğinde R'den başla)
  const baslangicFaz = anaKolonFazi === 'RST' ? 'R' : anaKolonFazi;
  const startX = centerX + 50;
  const startY = fazYPozisyonlari[baslangicFaz];

  // Bitiş noktası: merkezden (notr) 105 birim aşağı, başlangıçtan 50 birim sağa
  const endX = startX + 50; // centerX + 100
  const endY = centerY - 105;

  // Bağlantı noktaları (solid daireler) — seçilen faz(lar) + Nötr
  let dotFazlari;
  if (anaKolonFazi === 'RST') {
    dotFazlari = ['R', 'S', 'T', 'Notr'];
  } else {
    dotFazlari = [anaKolonFazi, 'Notr'];
  }
  dotFazlari.forEach(faz => {
    objects.push(createConnectionDot(startX, fazYPozisyonlari[faz], anaKolonColor));
  });

  // Dikey çizgi: başlangıçtan aşağıya (endY seviyesine)
  objects.push(createLine(
    startX, startY,
    startX, endY,
    anaKolonColor,
    { role: 'anaKolonVertical' }
  ));

  // Yatay çizgi: aşağı noktadan sağa (endX'e)
  objects.push(createLine(
    startX, endY,
    endX, endY,
    anaKolonColor,
    { role: 'anaKolonHorizontal' }
  ));

  // TMS Şalter (KacakAkimTMS2): bitiş noktasından 15 birim sağda
  const tmsX = endX + 15;
  const tmsY = endY;
  const tmsScale = 1;
  const tmsRotation = 0;
  const tmsOptions = {};
  let tmsEntities = KacakAkimTMS2(tmsX, tmsY, tmsScale, tmsRotation, tmsOptions);

  // Y eksenine simetri: X koordinatlarını tmsX merkezine göre aynala
  if (tmsSimetrik) {
    tmsEntities = tmsEntities.map(entity => {
      const mirrored = { ...entity };
      if (mirrored.vertices) {
        mirrored.vertices = mirrored.vertices.map(v => ({
          ...v,
          x: 2 * tmsX - v.x
        }));
      }
      if (mirrored.center) {
        mirrored.center = { ...mirrored.center, x: 2 * tmsX - mirrored.center.x };
      }
      return mirrored;
    });
  }

  const tmsObjects = convertDxfToThreeJS(tmsEntities, anaKolonColor);
  objects.push(...tmsObjects);

  // TMS üstüne üç satır yazı (x ekseninde ortalı, yukarıdan aşağıya)
  const labelFontSize = 12;
  const labelLineHeight = 14;
  const labelBaseY = tmsY + 30; // TMS üstünden başla
  objects.push(createTextSprite(
    '300ma K.A.K.R.',
    tmsX, labelBaseY + 2 * labelLineHeight,
    labelFontSize, '#ff0000'
  ));
  objects.push(createTextSprite(
    '4x100A TMŞ',
    tmsX, labelBaseY + 1 * labelLineHeight,
    labelFontSize, '#ff0000'
  ));
  objects.push(createTextSprite(
    '80-100A',
    tmsX, labelBaseY,
    labelFontSize, '#ff0000'
  ));

  // Ana kolon hattı: TMS basepoint'inden 15 birim sağdan itibaren 425 birim sağa yatay sürekli çizgi
  const anaKolonLineStartX = tmsX + 15;
  const anaKolonLineEndX = anaKolonLineStartX + 425;
  objects.push(createLine(
    anaKolonLineStartX, tmsY,
    anaKolonLineEndX, tmsY,
    anaKolonColor,
    { role: 'anaKolonMainLine' }
  ));

  // 425 birim çizginin sağ ucunun 20 birim soluna 72x36 dörtgen (sol kenar ortası çizgide)
  objects.push(createRectangle(
    anaKolonLineEndX - 20, tmsY + 18,
    72, 36,
    anaKolonColor
  ));

  // 36x72 dörtgenin üstünde 2 satır yazı (ortalanmış)
  const kofreMidX = anaKolonLineEndX - 20 + 36; // dörtgen yatay ortası
  const kofreTopY = tmsY + 18; // dörtgen üst kenarı
  objects.push(createTextSprite(
    'NH 63A',
    kofreMidX, kofreTopY + 8,
    12, '#ff0000'
  ));
  objects.push(createTextSprite(
    'Kofre',
    kofreMidX, kofreTopY + 22,
    12, '#ff0000'
  ));

  // 425 birim çizginin sağ ucunda 12 birimlik dik çizgi (ortası çizgide)
  objects.push(createLine(
    anaKolonLineEndX, tmsY + 6,
    anaKolonLineEndX, tmsY - 6,
    anaKolonColor,
    { role: 'anaKolonEndMark' }
  ));

  // 12 birim dik çizginin sağına anahtarlı otomatik sigorta
  const sigortaX = anaKolonLineEndX + 15;
  const sigortaY = tmsY;
  const sigortaEntities = AnahtarliOtomatikSigorta(sigortaX, sigortaY, 1, 3, 0, {}, true);
  const sigortaObjects = convertDxfToThreeJS(sigortaEntities, anaKolonColor);
  objects.push(...sigortaObjects);

  // Sigortanın sağında 2 birim boşlukla 12 birimlik dik çizgi (ortalı) ve sağa 50 birimlik yatay çizgi
  const sigortaEndX = sigortaX + 12; // sigorta sağ kenarı
  const dikCizgiX = sigortaEndX + 2;
  objects.push(createLine(
    dikCizgiX, tmsY + 6,
    dikCizgiX, tmsY - 6,
    anaKolonColor,
    { role: 'sigortaSonDikCizgi' }
  ));
  // Dik çizginin ortasından sağa 50 birimlik yatay çizgi
  objects.push(createLine(
    dikCizgiX, tmsY,
    dikCizgiX + 50, tmsY,
    anaKolonColor,
    { role: 'sigortaSonYatayCizgi' }
  ));

  // 50 birimlik yatay çizginin ucuna "YEDAŞ" yazısı (30 birim sağda)
  objects.push(createTextSprite(
    'YEDAŞ',
    dikCizgiX + 80, tmsY,
    12, '#ff0000'
  ));

  // 425 birim çizginin ortasına ana kolon kablosu yazısı (10 birim yukarıda, punto 12)
  const anaKolonHatti = kolonConfig.anaKolonHatti || {};
  const anaKolonKabloLabel = CABLES[anaKolonHatti.iletken]?.title || '4x16mm² NYY';
  const anaKolonLineMidX = anaKolonLineStartX + 425 / 2;
  objects.push(createTextSprite(
    anaKolonKabloLabel,
    anaKolonLineMidX, tmsY + 5,
    12, '#ff0000'
  ));

  // "4x16mm² NYY" yazısının 20 birim yukarısına "ɸ=100 Pe (kangal) boru" yazısı (punto 12)
  objects.push(createTextSprite(
    'ɸ=100 Pe (kangal) boru',
    anaKolonLineMidX, tmsY + 25,
    12, '#ff0000'
  ));

  // Boru sembolü: TMS basepoint'inden 100 birim sağda, boy=200, renk=cyan, kesikli çizgi
  const boruX = tmsX + 100;
  const boruY = tmsY;
  const boruScale = 1;
  const boruRotation = 0;
  const boruOptions = {};
  const boruColor = 0x00ffff; // Cyan
  const boruEntities = Boru(boruX, boruY, 200, boruScale, boruRotation, boruOptions);
  const boruObjects = convertDxfToThreeJS(boruEntities, boruColor, true);
  objects.push(...boruObjects);

  // Troid Bobin veya Acil Açma seçiliyse ortak elemanlar
  const hasTroid = kolonConfig.tmsTroidBobin || false;
  const hasAcil = kolonConfig.tmsAcilAcma || false;

  if (hasTroid || hasAcil) {
    // Ortak: TMS basepoint'inden dikey çizgi, 9 birim yukarı + 16 birim aşağı = 25 birim
    const lineStartY = tmsY + 9;
    const lineEndY = tmsY - 16;

    objects.push(createLine(
      tmsX, lineStartY,
      tmsX, lineEndY,
      anaKolonColor,
      { role: 'tmsOrtak' }
    ));

    // Ortak: TMS basepoint'inden 16 birim aşağıda merkezi olan 12 birim kenarı kare
    const sqCenterX = tmsX;
    const sqCenterY = tmsY - 16;
    const sqSide = 12;
    const sqHalf = sqSide / 2;

    objects.push(createRectangle(
      sqCenterX - sqHalf, sqCenterY + sqHalf,
      sqSide, sqSide,
      anaKolonColor
    ));

    // Ortak: Karenin ortasında yatay çizgi
    objects.push(createLine(
      sqCenterX - sqHalf, sqCenterY,
      sqCenterX + sqHalf, sqCenterY,
      anaKolonColor,
      { role: 'tmsOrtakHorizontal' }
    ));

    // Troid Bobin
    if (hasTroid) {
      // Karenin sağ ortasından sağa 30 birim, sonra yukarı 10 birim (iki kenarlı çizgi)
      const troidStartX = sqCenterX + sqHalf;
      const troidStartY = sqCenterY;
      const troidMidX = troidStartX + 30;
      const troidMidY = troidStartY;
      const troidEndX = troidMidX;
      const troidEndY = troidMidY + 10;

      // Yatay kısım
      objects.push(createLine(
        troidStartX, troidStartY,
        troidMidX, troidMidY,
        anaKolonColor,
        { role: 'troidBobinLineH' }
      ));
      // Dikey kısım
      objects.push(createLine(
        troidMidX, troidMidY,
        troidEndX, troidEndY,
        anaKolonColor,
        { role: 'troidBobinLineV' }
      ));

      // TMS basepoint'ten 36 birim sağda R=6 daire
      const troidCircleX = tmsX + 36;
      const troidCircleR = 6;
      objects.push(createHollowCircle(troidCircleX, tmsY, troidCircleR, anaKolonColor));

      // "Troid" yazısı: dairenin sağ üstünde
      objects.push(createTextSprite(
        'Troid',
        troidCircleX + troidCircleR + 12,
        tmsY + troidCircleR + 5,
        12,
        '#ff0000'
      ));
    }

    // Acil Açma Butonu
    if (hasAcil) {
      // Karenin alt kenarının ortasından 15 birim aşağı, sonra 100 birim sağa (iki kenarlı çizgi)
      const acilStartX = sqCenterX;
      const acilStartY = sqCenterY - sqHalf;
      const acilMidX = acilStartX;
      const acilMidY = acilStartY - 15;
      const acilEndX = acilMidX + 100;
      const acilEndY = acilMidY;

      // Dikey kısım
      objects.push(createLine(
        acilStartX, acilStartY,
        acilMidX, acilMidY,
        anaKolonColor,
        { role: 'acilAcmaLineV' }
      ));
      // Yatay kısım
      objects.push(createLine(
        acilMidX, acilMidY,
        acilEndX, acilEndY,
        anaKolonColor,
        { role: 'acilAcmaLineH' }
      ));

      // Çizgi ucuna kenarı 10 olan kare (sol kenarı çizgi ucunda)
      const acilSqSide = 10;
      const acilSqHalf = acilSqSide / 2;
      objects.push(createRectangle(
        acilEndX, acilEndY + acilSqHalf,
        acilSqSide, acilSqSide,
        anaKolonColor
      ));

      // Karenin ortası merkezi olan r=4 daire
      const acilCircleCenterX = acilEndX + acilSqHalf;
      const acilCircleCenterY = acilEndY;
      objects.push(createHollowCircle(acilCircleCenterX, acilCircleCenterY, 4, anaKolonColor));

      // "Acil Açma Butonu" yazısı: yatay çizginin sağından 15 birim sağda
      objects.push(createTextSprite(
        'Acil Açma Butonu',
        acilEndX + acilSqSide + 15 + 40,
        acilEndY,
        12,
        '#ff0000'
      ));
    }
  }

  // --- Sayaç hattı: faz iletkeni ---
  // Hat renkleri: faz barası rengi, RST ise kırmızı
  const fazRenkMap = {
    R: CONSTANTS.BAR_COLOR_R,
    S: CONSTANTS.BAR_COLOR_S,
    T: CONSTANTS.BAR_COLOR_T,
  };
  const fazHatColor = anaKolonFazi === 'RST' ? anaKolonColor : fazRenkMap[anaKolonFazi];
  const notrHatColor = CONSTANTS.BAR_COLOR_NOTR;
  const peHatColor = CONSTANTS.BAR_COLOR_PE;
  const enerjiKatIndex = tumKatlar ? tumKatlar.indexOf(kolonConfig.enerjiOdasiKati) : -1;
  const enerjiOdasiKati = kolonConfig.enerjiOdasiKati || 'Zemin Kat';

  // Toplam sayaç hesapla — panelsPerFloor varsa gerçek dağılımı kullan
  const panelsPerFloor = kolonConfig.panelsPerFloor || {};
  const hasPanelsPerFloor = kolonConfig.hasPanelData || Object.keys(panelsPerFloor).length > 0;
  const enerjiKatHasPanel = (panelsPerFloor[enerjiOdasiKati] || 0) > 0;
  const toplamKat = (parseInt(kolonConfig.bodrumKat) || 0)
    + 1 // zemin kat
    + (kolonConfig.asmaKat ? 1 : 0)
    + (kolonConfig.galeriKat ? 1 : 0)
    + (parseInt(kolonConfig.normalKat) || 0)
    + (kolonConfig.catiKat ? 1 : 0);
  const katBasiDaire = parseInt(kolonConfig.katBasiDaire) || 1;
  const sayacAdet = hasPanelsPerFloor
    ? tumKatlar.reduce((sum, kat) => sum + (panelsPerFloor[kat] || 0), 0)
    : toplamKat * katBasiDaire;

  // Her sayacın hangi kata ait olduğunu hesapla (ters sıra: en üst kat solda)
  // tumKatlar: aşağıdan yukarıya sıralı → ters çevirip soldan sağa yerleştir
  const sayacKatMapping = []; // [katIndex, katIndex, ...] — her sayaç için kat index'i
  if (hasPanelsPerFloor) {
    // Yukarıdan aşağıya sırayla (ters) her kat için o kattaki panel sayısı kadar ekle
    const reversedKatlar = [...tumKatlar].reverse();
    reversedKatlar.forEach(kat => {
      const count = panelsPerFloor[kat] || 0;
      const katIndex = tumKatlar.indexOf(kat);
      for (let i = 0; i < count; i++) {
        sayacKatMapping.push(katIndex);
      }
    });
  }

  // katlar yapısından düz panel listesi oluştur (üst kattan alta, generate'ın beklediği sırada)
  const katlarData = kolonConfig.katlar || [];
  const hasKatlar = katlarData.length > 0 && katlarData.some(k => k.paneller?.length > 0);
  let allPanels = []; // { sayacKesici, panelKesici, iletken, guc, faz, aboneTipi, katIndex, katName }
  if (hasKatlar) {
    const reversedKatlarData = [...katlarData].sort((a, b) => b.katNo - a.katNo);
    reversedKatlarData.forEach(kat => {
      (kat.paneller || []).forEach(p => {
        // Müşterek (GT) panel zaten bara sonunda ayrıca çiziliyor, tekrar ekleme
        if (p.kullanimAmaci === 'Müşterek' || p.aboneTipi === 'Müşterek') return;
        allPanels.push({ ...p, katIndex: kat.katNo, katName: kat.name });
      });
    });
  }

  const sayacAralik = 100; // sayaçlar arası mesafe
  const rBarY = fazYPozisyonlari['R'];
  const fazHatCssColor = '#' + fazHatColor.toString(16).padStart(6, '0');
  const sayacFazlari = anaKolonFazi === 'RST' ? ['R', 'S', 'T'] : [anaKolonFazi];
  const sayacFazY = fazYPozisyonlari[sayacFazlari[sayacFazlari.length - 1]]; // en alt fazdan başla
  const sayacSigortaPolar = anaKolonFazi === 'RST' ? 3 : 1;
  const sayacNotrFaz = anaKolonFazi === 'RST' ? 3 : 1;

  for (let si = 0; si < sayacAdet; si++) {
    const sayacHatStartX = startX + 100 + si * sayacAralik; // her sayaç 100 birim sağa
    const panelData = hasKatlar ? allPanels[si] : null;
    const sayacKesiciLabel = panelData ? (MCB[panelData.sayacKesici]?.title || 'C-3X32A') : 'C-3X32A';
    const panelKesiciLabel = panelData ? (MCB[panelData.panelKesici]?.title || 'C-3X32A') : 'C-3X32A';
    const iletkenLabel = panelData ? (CABLES[panelData.iletken]?.title || '5x6 mm² NYM-J') : '5x6 mm² NYM-J';

    // --- Faz hattı ---
    // Faz baraları üzerinde bağlantı noktaları
    sayacFazlari.forEach(faz => {
      objects.push(createConnectionDot(sayacHatStartX, fazYPozisyonlari[faz], fazHatColor));
    });

    // Faz iletkenini R fazının 15 birim üstüne kadar çıkar
    const sayacDikEndY = rBarY + 15;
    objects.push(createLine(
      sayacHatStartX, sayacFazY,
      sayacHatStartX, sayacDikEndY,
      fazHatColor,
      { role: 'sayacFazDikey' }
    ));

    // 90 derece döndürülmüş AnahtarliOtomatikSigorta (dairesiz)
    const sayacSigortaX = sayacHatStartX;
    const sayacSigortaY = sayacDikEndY + 12;
    const sayacSigortaEntities = AnahtarliOtomatikSigorta(sayacSigortaX, sayacSigortaY, 1, sayacSigortaPolar, 90, {}, true);
    const sayacSigortaObjects = convertDxfToThreeJS(sayacSigortaEntities, fazHatColor);
    objects.push(...sayacSigortaObjects);

    // Sayaç sigortasının soluna iki satır yazı
    objects.push(createTextSprite(
      sayacKesiciLabel,
      sayacSigortaX - 35, sayacSigortaY + 7,
      10, fazHatCssColor
    ));
    objects.push(createTextSprite(
      '10kA',
      sayacSigortaX - 35, sayacSigortaY - 7,
      10, fazHatCssColor
    ));

    // Sigortanın üst tarafından 40 birimlik dik çizgi (90° döndürülmüş: üst = Y + 12)
    const sayacSigortaTopY = sayacSigortaY + 12;
    objects.push(createLine(
      sayacHatStartX, sayacSigortaTopY,
      sayacHatStartX, sayacSigortaTopY + 40,
      fazHatColor,
      { role: 'sayacSigortaUstDik' }
    ));

    // 40 birimlik çizginin sonunda sola doğru 10 birim çizgi
    objects.push(createLine(
      sayacHatStartX, sayacSigortaTopY + 40,
      sayacHatStartX - 10, sayacSigortaTopY + 40,
      fazHatColor,
      { role: 'sayacSigortaSolaYatay' }
    ));

    // --- Nötr hattı (kesikli) ---
    const notrHatX = sayacHatStartX + 10; // faz hattından 10 birim sağda
    const notrBarY = fazYPozisyonlari['Notr'];
    const notrDikEndY = rBarY + 110; // R fazının 110 birim üstü

    // Nötr barası üzerinde bağlantı noktası
    objects.push(createConnectionDot(notrHatX, notrBarY, notrHatColor));

    // Nötr dikey kesikli çizgi: baradan yukarı
    objects.push(createDashedLine(
      notrHatX, notrBarY,
      notrHatX, notrDikEndY,
      notrHatColor,
      { role: 'notrDikey' }
    ));

    // Üst ucundan 20 birim aşağıdan sola doğru 20 birimlik kesikli ek (uçlar solid, gap ortada)
    const notrEkY = notrDikEndY - 20;
    // 8 solid + 4 gap + 8 solid = 20
    objects.push(createLine(
      notrHatX, notrEkY,
      notrHatX - 8, notrEkY,
      notrHatColor,
      { role: 'notrEkYatay1' }
    ));
    objects.push(createLine(
      notrHatX - 12, notrEkY,
      notrHatX - 20, notrEkY,
      notrHatColor,
      { role: 'notrEkYatay2' }
    ));

    // Ek noktasına bağlantı buatı (çizginin sağ ucunda = dikey hat üzerinde)
    objects.push(createConnectionDot(notrHatX, notrEkY, notrHatColor));

    // 20 birimlik nötr çizgisinin sol ucuna 90° sayaç (sol kenar ortası çizginin sol ucunda)
    const sayacNotrX = notrHatX - 20 + 20;
    const sayacNotrY = notrEkY;
    const sayacNotrEntities = Sayac(sayacNotrX - 40, sayacNotrY, 1, sayacNotrFaz, 90, {});
    const sayacNotrObjects = convertDxfToThreeJS(sayacNotrEntities, fazHatColor);
    objects.push(...sayacNotrObjects);

    // Nötr ek çizgisinin 10 birim üstünde, sol ucundan sağa 10 birim yatay + ucundan yukarı 10 birim dikey
    const notrUstY = notrEkY + 10;
    const notrUstSolX = notrHatX - 20;
    objects.push(createLine(
      notrUstSolX, notrUstY,
      notrUstSolX + 10, notrUstY,
      fazHatColor,
      { role: 'notrUstYatay' }
    ));
    objects.push(createLine(
      notrUstSolX + 10, notrUstY,
      notrUstSolX + 10, notrUstY + 10,
      fazHatColor,
      { role: 'notrUstDikey' }
    ));

    // --- Pe hattı ---
    const peHatX = notrHatX + 10; // Nötr iletkeninin 10 birim sağında
    const peBarY = centerY - 2 * CONSTANTS.BAR_SPACING;

    // Pe barası üzerinde bağlantı noktası
    objects.push(createConnectionDot(peHatX, peBarY, peHatColor));

    // Pe dikey çizgi: baradan nötr ile aynı Y seviyesine
    objects.push(createLine(
      peHatX, peBarY,
      peHatX, notrDikEndY,
      peHatColor,
      { role: 'peDikey' }
    ));

    // Üst ucundan sola doğru 20 birimlik yatay çizgiler (3'er birim arayla 3 adet, faz renginde)
    for (let i = 0; i < 3; i++) {
      objects.push(createLine(
        peHatX, notrDikEndY + i * 3,
        peHatX - 20, notrDikEndY + i * 3,
        fazHatColor,
        { role: `peYatay${si}_${i}` }
      ));
    }

    // --- Panel bağlantı hattı ---
    // 3 adet 20 birimlik çizgilerin ortasından dikey hat
    const panelHatX = peHatX - 10; // 20 birimlik çizgilerin ortası
    const panelHatStartY = notrDikEndY + 3; // ortadaki çizginin Y'si
    // katlar yapısı varsa oradan, panelsPerFloor varsa mapping'den, yoksa eski uniform hesap
    const katIndex = hasKatlar
      ? (allPanels[si]?.katIndex ?? 0)
      : sayacKatMapping.length > 0
        ? (sayacKatMapping[si] ?? 0)
        : toplamKat - 1 - Math.floor(si / katBasiDaire);
    if (katIndex >= enerjiKatIndex) {
      // --- Enerji odası katı ve ÜSTÜ: yukarı yönde panel bağlantısı ---
      // Enerji odası panelleri: base yükseklik (140), üst katlar: enerji odası panel varsa 1 kat kayık
      const floorOffset = katIndex - enerjiKatIndex; // 0 = enerji katı, 1 = ilk üst kat, ...
      const effectiveOffset = enerjiKatHasPanel ? floorOffset : (floorOffset - 1);
      const panelHatEndY = panelHatStartY + 140 + effectiveOffset * 150;

      objects.push(createLine(
        panelHatX, panelHatStartY,
        panelHatX, panelHatEndY,
        fazHatColor,
        { role: `panelDikey_${si}` }
      ));

      // Çizginin soluna iletken bilgisi (90 derece, tek satır)
      const kabloBilgiSprite = createTextSprite(
        iletkenLabel,
        panelHatX - 5, panelHatStartY + 70,
        10, fazHatCssColor
      );
      kabloBilgiSprite.material.rotation = (90 * Math.PI) / 180;
      objects.push(kabloBilgiSprite);

      // Çizginin ucuna 90° AnahtarliOtomatikSigorta (alt kenarı çizgi ucunda)
      const panelSigortaX = panelHatX;
      const panelSigortaY = panelHatEndY + 12;
      const panelSigortaEntities = AnahtarliOtomatikSigorta(panelSigortaX, panelSigortaY, 1, sayacSigortaPolar, 90, {}, true);
      const panelSigortaObjects = convertDxfToThreeJS(panelSigortaEntities, fazHatColor);
      objects.push(...panelSigortaObjects);

      // Panel sigortasının soluna iki satır yazı
      objects.push(createTextSprite(
        panelKesiciLabel,
        panelSigortaX - 35, panelSigortaY + 7,
        10, fazHatCssColor
      ));
      objects.push(createTextSprite(
        '10kA',
        panelSigortaX - 35, panelSigortaY - 7,
        10, fazHatCssColor
      ));

      // Sigortadan sonra yukarı doğru 30 birim çizgi
      const panelSigortaTopY = panelSigortaY + 12;
      objects.push(createLine(
        panelHatX, panelSigortaTopY,
        panelHatX, panelSigortaTopY + 30,
        fazHatColor,
        { role: `panelSigortaUst_${si}` }
      ));

      // 30 birim çizginin ucuna 90° KacakAkim koruma rölesi
      const kacakAkimY = panelSigortaTopY + 30 + 8;
      const kacakAkimEntities = KacakAkim(panelHatX, kacakAkimY, 1, 90, {});
      const kacakAkimObjects = convertDxfToThreeJS(kacakAkimEntities, fazHatColor);
      objects.push(...kacakAkimObjects);

      // Kaçak akım rölesinin soluna üç satır yazı
      objects.push(createTextSprite(
        'K.A.K.R.',
        panelHatX - 35, kacakAkimY + 10,
        10, fazHatCssColor
      ));
      objects.push(createTextSprite(
        '4P-300mA',
        panelHatX - 35, kacakAkimY,
        10, fazHatCssColor
      ));
      objects.push(createTextSprite(
        '25A',
        panelHatX - 35, kacakAkimY - 10,
        10, fazHatCssColor
      ));

      // KacakAkim sonrasına 20 birim dik çizgi
      const kacakAkimTopY = kacakAkimY + 8;
      objects.push(createLine(
        panelHatX, kacakAkimTopY,
        panelHatX, kacakAkimTopY + 20,
        fazHatColor,
        { role: `kacakAkimUst_${si}` }
      ));

      // Panel (PanoTali)
      const panelWidth = 65;
      const panelHeight = 18;
      const panelTopY = kacakAkimTopY + 20;
      const panelCenterY = panelTopY + panelHeight / 2;
      const panoEntities = PanoTali(panelHatX, panelCenterY, panelWidth, panelHeight, 1, 0, {});
      const panoObjects = convertDxfToThreeJS(panoEntities, fazHatColor);
      objects.push(...panoObjects);

      // Panelin üstüne 2 satır yazı
      const panelUstY = panelCenterY + panelHeight / 2;
      objects.push(createTextSprite(
        `${panelData?.guc || 0}W`,
        panelHatX, panelUstY + 8,
        10, fazHatCssColor
      ));
      objects.push(createTextSprite(
        panelData?.name || 'Z.K.T.',
        panelHatX, panelUstY + 20,
        10, fazHatCssColor
      ));
    } else {
      // --- Enerji odası ALTI: aşağı yönde panel bağlantısı (20 birim sağa kayık) ---
      const panelHatXDown = panelHatX + 20;
      const bridgeTopY = panelHatStartY + 20; // 20 birim yukarı
      const panelHatEndY = panelHatStartY - (380 + (enerjiKatIndex - katIndex) * 150);

      // 1. Yukarı 20 birim
      objects.push(createLine(
        panelHatX, panelHatStartY,
        panelHatX, bridgeTopY,
        fazHatColor,
        { role: `panelBridgeUp_${si}` }
      ));

      // 2. Sağa 20 birim (yatay köprü)
      objects.push(createLine(
        panelHatX, bridgeTopY,
        panelHatXDown, bridgeTopY,
        fazHatColor,
        { role: `panelBridgeRight_${si}` }
      ));

      // 3. Aşağı 20 birim (panelHatStartY seviyesine geri dön)
      objects.push(createLine(
        panelHatXDown, bridgeTopY,
        panelHatXDown, panelHatStartY,
        fazHatColor,
        { role: `panelBridgeDown_${si}` }
      ));

      // 4. Aşağı 331 birim
      objects.push(createLine(
        panelHatXDown, panelHatStartY,
        panelHatXDown, panelHatEndY,
        fazHatColor,
        { role: `panelDikey_${si}` }
      ));

      // Çizginin soluna iletken bilgisi (90 derece, tek satır)
      const kabloBilgiSprite = createTextSprite(
        iletkenLabel,
        panelHatXDown - 5, panelHatStartY - 70,
        10, fazHatCssColor
      );
      kabloBilgiSprite.material.rotation = (90 * Math.PI) / 180;
      objects.push(kabloBilgiSprite);

      // Çizginin ucuna 90° AnahtarliOtomatikSigorta (aşağıda)
      const panelSigortaX = panelHatXDown;
      const panelSigortaY = panelHatEndY - 12;
      const panelSigortaEntities = AnahtarliOtomatikSigorta(panelSigortaX, panelSigortaY, 1, sayacSigortaPolar, 90, {}, true);
      const panelSigortaObjects = convertDxfToThreeJS(panelSigortaEntities, fazHatColor);
      objects.push(...panelSigortaObjects);

      // Panel sigortasının soluna iki satır yazı
      objects.push(createTextSprite(
        panelKesiciLabel,
        panelSigortaX - 35, panelSigortaY + 7,
        10, fazHatCssColor
      ));
      objects.push(createTextSprite(
        '10kA',
        panelSigortaX - 35, panelSigortaY - 7,
        10, fazHatCssColor
      ));

      // Sigortadan sonra aşağı doğru 30 birim çizgi
      const panelSigortaBottomY = panelSigortaY - 12;
      objects.push(createLine(
        panelHatXDown, panelSigortaBottomY,
        panelHatXDown, panelSigortaBottomY - 30,
        fazHatColor,
        { role: `panelSigortaAlt_${si}` }
      ));

      // 30 birim çizginin ucuna 90° KacakAkim koruma rölesi
      const kacakAkimY = panelSigortaBottomY - 30 - 8;
      const kacakAkimEntities = KacakAkim(panelHatXDown, kacakAkimY, 1, 90, {});
      const kacakAkimObjects = convertDxfToThreeJS(kacakAkimEntities, fazHatColor);
      objects.push(...kacakAkimObjects);

      // Kaçak akım rölesinin soluna üç satır yazı
      objects.push(createTextSprite(
        'K.A.K.R.',
        panelHatXDown - 35, kacakAkimY + 10,
        10, fazHatCssColor
      ));
      objects.push(createTextSprite(
        '4P-300mA',
        panelHatXDown - 35, kacakAkimY,
        10, fazHatCssColor
      ));
      objects.push(createTextSprite(
        '25A',
        panelHatXDown - 35, kacakAkimY - 10,
        10, fazHatCssColor
      ));

      // KacakAkim sonrasına 20 birim aşağı dik çizgi
      const kacakAkimBottomY = kacakAkimY - 8;
      objects.push(createLine(
        panelHatXDown, kacakAkimBottomY,
        panelHatXDown, kacakAkimBottomY - 20,
        fazHatColor,
        { role: `kacakAkimAlt_${si}` }
      ));

      // Panel (PanoTali)
      const panelWidth = 65;
      const panelHeight = 18;
      const panelBottomY = kacakAkimBottomY - 20;
      const panelCenterY = panelBottomY - panelHeight / 2;
      const panoEntities = PanoTali(panelHatXDown, panelCenterY, panelWidth, panelHeight, 1, 0, {});
      const panoObjects = convertDxfToThreeJS(panoEntities, fazHatColor);
      objects.push(...panoObjects);

      // Panelin altına 2 satır yazı
      const panelAltY = panelCenterY - panelHeight / 2;
      objects.push(createTextSprite(
        `${panelData?.guc || 0}W`,
        panelHatXDown, panelAltY - 8,
        10, fazHatCssColor
      ));
      objects.push(createTextSprite(
        panelData?.name || 'Z.K.T.',
        panelHatXDown, panelAltY - 20,
        10, fazHatCssColor
      ));
    }
  }

  // --- GT (Genel Tablo) Sayacı: tüm sayaçların sağına ---
  if (kolonConfig.hasGT) {
  const barEndX = centerX + barWidth;
  const gtStartX = barEndX - 50;

  // Faz bağlantı noktaları
  sayacFazlari.forEach(faz => {
    objects.push(createConnectionDot(gtStartX, fazYPozisyonlari[faz], fazHatColor));
  });

  // Faz iletkenini R fazının 15 birim üstüne kadar çıkar
  const gtDikEndY = rBarY + 15;
  objects.push(createLine(
    gtStartX, sayacFazY,
    gtStartX, gtDikEndY,
    fazHatColor,
    { role: 'gtFazDikey' }
  ));

  // 90° AnahtarliOtomatikSigorta
  const gtSigortaX = gtStartX;
  const gtSigortaY = gtDikEndY + 12;
  const gtSigortaEntities = AnahtarliOtomatikSigorta(gtSigortaX, gtSigortaY, 1, sayacSigortaPolar, 90, {}, true);
  const gtSigortaObjects = convertDxfToThreeJS(gtSigortaEntities, fazHatColor);
  objects.push(...gtSigortaObjects);

  // Sigorta yazıları
  objects.push(createTextSprite(
    'C-3X32A',
    gtSigortaX - 35, gtSigortaY + 7,
    10, fazHatCssColor
  ));
  objects.push(createTextSprite(
    '10kA',
    gtSigortaX - 35, gtSigortaY - 7,
    10, fazHatCssColor
  ));

  // Sigortanın üstünden 40 birim dik çizgi
  const gtSigortaTopY = gtSigortaY + 12;
  objects.push(createLine(
    gtStartX, gtSigortaTopY,
    gtStartX, gtSigortaTopY + 40,
    fazHatColor,
    { role: 'gtSigortaUstDik' }
  ));

  // 40 birimlik çizginin sonunda sola doğru 10 birim çizgi
  objects.push(createLine(
    gtStartX, gtSigortaTopY + 40,
    gtStartX - 10, gtSigortaTopY + 40,
    fazHatColor,
    { role: 'gtSigortaSolaYatay' }
  ));

  // --- GT Nötr hattı (kesikli) ---
  const gtNotrHatX = gtStartX + 10;
  const gtNotrBarY = fazYPozisyonlari['Notr'];
  const gtNotrDikEndY = rBarY + 110;

  objects.push(createConnectionDot(gtNotrHatX, gtNotrBarY, notrHatColor));

  objects.push(createDashedLine(
    gtNotrHatX, gtNotrBarY,
    gtNotrHatX, gtNotrDikEndY,
    notrHatColor,
    { role: 'gtNotrDikey' }
  ));

  // Nötr ek çizgisi
  const gtNotrEkY = gtNotrDikEndY - 20;
  objects.push(createLine(
    gtNotrHatX, gtNotrEkY,
    gtNotrHatX - 8, gtNotrEkY,
    notrHatColor,
    { role: 'gtNotrEkYatay1' }
  ));
  objects.push(createLine(
    gtNotrHatX - 12, gtNotrEkY,
    gtNotrHatX - 20, gtNotrEkY,
    notrHatColor,
    { role: 'gtNotrEkYatay2' }
  ));
  objects.push(createConnectionDot(gtNotrHatX, gtNotrEkY, notrHatColor));

  // Sayaç sembolü
  const gtSayacEntities = Sayac(gtNotrHatX - 20 + 20 - 40, gtNotrEkY, 1, sayacNotrFaz, 90, {});
  const gtSayacObjects = convertDxfToThreeJS(gtSayacEntities, fazHatColor);
  objects.push(...gtSayacObjects);

  // Nötr ek çizgisinin üstünde yatay+dikey kol
  const gtNotrUstY = gtNotrEkY + 10;
  const gtNotrUstSolX = gtNotrHatX - 20;
  objects.push(createLine(
    gtNotrUstSolX, gtNotrUstY,
    gtNotrUstSolX + 10, gtNotrUstY,
    fazHatColor,
    { role: 'gtNotrUstYatay' }
  ));
  objects.push(createLine(
    gtNotrUstSolX + 10, gtNotrUstY,
    gtNotrUstSolX + 10, gtNotrUstY + 10,
    fazHatColor,
    { role: 'gtNotrUstDikey' }
  ));

  // --- GT Pe hattı ---
  const gtPeHatX = gtNotrHatX + 10;
  const gtPeBarY = centerY - 2 * CONSTANTS.BAR_SPACING;

  objects.push(createConnectionDot(gtPeHatX, gtPeBarY, peHatColor));

  objects.push(createLine(
    gtPeHatX, gtPeBarY,
    gtPeHatX, gtNotrDikEndY,
    peHatColor,
    { role: 'gtPeDikey' }
  ));

  // Pe üst ucunda 3 adet yatay çizgi
  for (let i = 0; i < 3; i++) {
    objects.push(createLine(
      gtPeHatX, gtNotrDikEndY + i * 3,
      gtPeHatX - 20, gtNotrDikEndY + i * 3,
      fazHatColor,
      { role: `gtPeYatay_${i}` }
    ));
  }

  // --- GT Çıkış iletkeni: alt kata giden iletken gibi ---
  // 3 yatay çizginin ortasından başla
  const gtPanelHatX = gtPeHatX - 10;
  const gtPanelHatStartY = gtNotrDikEndY + 3;
  const gtPanelHatXDown = gtPanelHatX + 20;
  const gtBridgeTopY = gtPanelHatStartY + 20;

  // 1. Yukarı 20 birim
  objects.push(createLine(
    gtPanelHatX, gtPanelHatStartY,
    gtPanelHatX, gtBridgeTopY,
    fazHatColor,
    { role: 'gtPanelBridgeUp' }
  ));

  // 2. Sağa 20 birim
  objects.push(createLine(
    gtPanelHatX, gtBridgeTopY,
    gtPanelHatXDown, gtBridgeTopY,
    fazHatColor,
    { role: 'gtPanelBridgeRight' }
  ));

  // 3. Sigorta (yatay, rotation=0)
  const gtCikisSigortaX = gtPanelHatXDown + 12;
  const gtCikisSigortaY = gtBridgeTopY;
  const gtCikisSigortaEntities = AnahtarliOtomatikSigorta(gtCikisSigortaX, gtCikisSigortaY, 1, sayacSigortaPolar, 0, {}, true);
  const gtCikisSigortaObjects = convertDxfToThreeJS(gtCikisSigortaEntities, fazHatColor);
  objects.push(...gtCikisSigortaObjects);

  // Sigorta yazıları (üstünde)
  objects.push(createTextSprite(
    'C-3X32A',
    gtCikisSigortaX, gtCikisSigortaY + 14,
    10, fazHatCssColor
  ));
  objects.push(createTextSprite(
    '10kA',
    gtCikisSigortaX, gtCikisSigortaY + 26,
    10, fazHatCssColor
  ));

  // 4. Sigortadan sonra 30 birim sağa çizgi
  const gtSigortaEndX = gtCikisSigortaX + 12;
  objects.push(createLine(
    gtSigortaEndX, gtCikisSigortaY,
    gtSigortaEndX + 30, gtCikisSigortaY,
    fazHatColor,
    { role: 'gtSigortaSonraYatay' }
  ));

  // 5. KAKR (yatay, rotation=0)
  const gtKakrX = gtSigortaEndX + 30 + 8;
  const gtKakrY = gtCikisSigortaY;
  const gtKakrEntities = KacakAkim(gtKakrX, gtKakrY, 1, 0, {});
  const gtKakrObjects = convertDxfToThreeJS(gtKakrEntities, fazHatColor);
  objects.push(...gtKakrObjects);

  // KAKR yazıları (üstünde)
  objects.push(createTextSprite(
    'K.A.K.R.',
    gtKakrX, gtKakrY + 14,
    10, fazHatCssColor
  ));
  objects.push(createTextSprite(
    '4P-300mA',
    gtKakrX, gtKakrY + 26,
    10, fazHatCssColor
  ));
  objects.push(createTextSprite(
    '25A',
    gtKakrX, gtKakrY + 38,
    10, fazHatCssColor
  ));

  // 6. KAKR sonrası 20 birim sağa çizgi
  const gtKakrEndX = gtKakrX + 8;
  objects.push(createLine(
    gtKakrEndX, gtKakrY,
    gtKakrEndX + 20, gtKakrY,
    fazHatColor,
    { role: 'gtKakrSonraYatay' }
  ));

  // 7. Aşağı 136 birim
  const gtDikStartX = gtKakrEndX + 20;
  const gtPanelDikEndY = gtKakrY - 136;
  objects.push(createLine(
    gtDikStartX, gtKakrY,
    gtDikStartX, gtPanelDikEndY,
    fazHatColor,
    { role: 'gtPanelDikey' }
  ));

  // 8. Sağa 30 birim
  objects.push(createLine(
    gtDikStartX, gtPanelDikEndY,
    gtDikStartX + 30, gtPanelDikEndY,
    fazHatColor,
    { role: 'gtPanelSagaYatay' }
  ));

  // --- GT Toplam Çıkış Hesapla ---
  const gtMerdivenCikis = parseInt(kolonConfig.gtMerdivenCikis) || 0;
  const gtAsansorCikis = parseInt(kolonConfig.gtAsansorCikis) || 0;
  // GT tali panel sayısı (checkbox veya gerçek panellerden)
  const gtCheckboxKeys = ['asansor', 'kazanDairesi', 'siginak', 'otopark', 'mustemilat', 'yanginIhbar'];
  let gtCheckboxToplam = 0;
  if (kolonConfig.gtTaliPaneller && kolonConfig.gtTaliPaneller.length > 0) {
    gtCheckboxToplam = kolonConfig.gtTaliPaneller.length;
  } else if (kolonConfig.gtConfig) {
    Object.values(kolonConfig.gtConfig).forEach(katConfig => {
      gtCheckboxKeys.forEach(key => {
        if (katConfig[key]) gtCheckboxToplam++;
      });
    });
  }
  // Zayıf akım (interkon): şimdilik devre dışı — GT'ye interkon tesisatı eklenemiyor
  const zayifAkimVar = false;
  const gtToplamCikis = gtMerdivenCikis + gtAsansorCikis + gtCheckboxToplam + (zayifAkimVar ? 1 : 0);

  // 9. PanoTali (270°) — genişlik dinamik (minimum 20 birim, çıkış olmasa bile görünür)
  const gtPanoX = gtDikStartX + 39;
  const gtPanoY = gtPanelDikEndY;
  const gtPanoWidth = Math.max(gtToplamCikis * 20, 20);
  const gtPanoHeight = 18;
  const gtPanoEntities = PanoTali(gtPanoX, gtPanoY, gtPanoWidth, gtPanoHeight, 1, 270, {});
  const gtPanoObjects = convertDxfToThreeJS(gtPanoEntities, fazHatColor);
  objects.push(...gtPanoObjects);

  // Panelin üstüne yazılar
  objects.push(createTextSprite(
    '1234 W',
    gtPanoX, gtPanoY + gtPanoWidth / 2 + 8,
    10, fazHatCssColor
  ));
  objects.push(createTextSprite(
    'G.T.',
    gtPanoX, gtPanoY + gtPanoWidth / 2 + 20,
    10, fazHatCssColor
  ));

  // 10. GT Çıkışları — panelin sağ tarafından, her biri 20 birim arayla
  // Panel 270° döndürülmüş: genişlik Y ekseni boyunca (yukarıdan aşağıya)
  // Panelin sağ kenarı: gtPanoX + gtPanoHeight/2
  const gtCikisStartX = gtPanoX + gtPanoHeight / 2;
  const gtCikisTopY = gtPanoY + gtPanoWidth / 2 - 10; // ilk çıkış: üstten 10 birim içeride

  // --- Çıkış sıralaması belirleme ---
  // Yukarıdan aşağıya: üst kat tali panelleri, merdiven aydınlatma, asansör aydınlatma, zayıf akım, alt kat tali panelleri
  const enerjiKatIdx = tumKatlar.indexOf(kolonConfig.enerjiOdasiKati);
  const gtCikisColor = 0xffff00; // Sarı — merdiven/asansör aydınlatma
  const gtCikisCssColor = '#ffff00';

  // Tali panelleri topla (yukarı/aşağı ayır)
  const taliPanelCheckboxKeys = ['asansor', 'kazanDairesi', 'siginak', 'otopark', 'mustemilat', 'yanginIhbar'];
  const ustTaliPaneller = []; // enerji odası üstündeki katlar
  const altTaliPaneller = []; // enerji odası katı ve altındaki katlar
  if (kolonConfig.gtTaliPaneller && kolonConfig.gtTaliPaneller.length > 0) {
    // Gerçek GT child panellerinden (supply data)
    kolonConfig.gtTaliPaneller.forEach(tali => {
      const katIdx = tumKatlar.indexOf(tali.kat);
      if (katIdx < 0) return;
      if (katIdx > enerjiKatIdx) {
        ustTaliPaneller.push(tali);
      } else {
        altTaliPaneller.push(tali);
      }
    });
  } else if (kolonConfig.gtConfig) {
    // Fallback: checkbox tabanlı (eski yöntem)
    tumKatlar.forEach((kat, katIdx) => {
      const katConfig = kolonConfig.gtConfig[kat];
      if (!katConfig) return;
      taliPanelCheckboxKeys.forEach(key => {
        if (katConfig[key]) {
          const label = { asansor: 'Asansör', kazanDairesi: 'Kazan Dairesi', siginak: 'Sığınak', otopark: 'Otopark', mustemilat: 'Müştemilat', yanginIhbar: 'Yangın İhbar' }[key];
          if (katIdx > enerjiKatIdx) {
            ustTaliPaneller.push({ kat, key, label });
          } else {
            altTaliPaneller.push({ kat, key, label });
          }
        }
      });
    });
  }
  // Üst tali panelleri yukarıdan aşağıya sırala (en üst kat GT'nin en üstünde)
  ustTaliPaneller.reverse();
  // Alt tali panelleri: enerji odası katı en üstte (zayıf akımın hemen altında), en alt kat en altta
  altTaliPaneller.reverse();

  // Jeneratör olan katları bul (transfer paneli için)
  const jeneratorVar = !!kolonConfig.siginakJenerator;
  const jeneratorSutunGenisligi = jeneratorVar ? 100 : 0;

  // Tali panel sütunu var mı?
  const hasTaliPaneller = (ustTaliPaneller.length + altTaliPaneller.length) > 0;
  const taliSutunGenisligi = hasTaliPaneller ? 310 : 0;

  // Çıkış slot sıralaması: üst tali paneller → merdiven → asansör → zayıf akım → alt tali paneller
  const ustTaliSlotCount = ustTaliPaneller.length;
  const merdivenSlotStart = ustTaliSlotCount;
  const asansorSlotStart = merdivenSlotStart + gtMerdivenCikis;
  const zayifAkimSlotIdx = asansorSlotStart + gtAsansorCikis;
  const altTaliSlotStart = zayifAkimSlotIdx + (zayifAkimVar ? 1 : 0);

  // Tüm çıkışlar için 20 birim çizgi + sigorta çiz
  for (let ci = 0; ci < gtToplamCikis; ci++) {
    const cikisY = gtCikisTopY - ci * 20;

    // 20 birim sağa çizgi
    objects.push(createLine(
      gtCikisStartX, cikisY,
      gtCikisStartX + 20, cikisY,
      fazHatColor,
      { role: `gtCikis_${ci}` }
    ));

    // Sigorta (yatay, rotation=0)
    const gtCikisSigX = gtCikisStartX + 20 + 12;
    const gtCikisSigEntities = AnahtarliOtomatikSigorta(gtCikisSigX, cikisY, 1, 1, 0, {}, true);
    const gtCikisSigObjects = convertDxfToThreeJS(gtCikisSigEntities, fazHatColor);
    objects.push(...gtCikisSigObjects);

    // Sigorta yazıları (sağında, çizginin üstü ve altı)
    objects.push(createTextSprite(
      'C-1X16A',
      gtCikisSigX + 12 + 17, cikisY + 4,
      8, fazHatCssColor
    ));
    objects.push(createTextSprite(
      '10kA',
      gtCikisSigX + 12 + 17, cikisY - 5,
      8, fazHatCssColor
    ));
  }

  // --- Merdiven & Asansör aydınlatma hatları (sarı) ---
  const gtSigortaEndXBase = gtCikisStartX + 20 + 12 + 12; // sigorta sağ ucu
  const gtMerdivenKatlar = kolonConfig.gtMerdivenKatlar || {};
  const gtAsansorKatlar = kolonConfig.gtAsansorKatlar || {};

  // Enerji odası katının orta Y seviyesi (yukarı/aşağı ayrımı için)
  const enerjiKatPos = katYPositions?.[kolonConfig.enerjiOdasiKati];
  const enerjiKatMidY = enerjiKatPos ? (enerjiKatPos.top + enerjiKatPos.bottom) / 2 : 0;

  // Tüm aydınlatma çıkışlarını topla (merdiven + asansör)
  // Her çıkış: { tip, idx, cikisSlot, katlar }
  const aydinlatmaCikislari = [];
  for (let mi = 0; mi < gtMerdivenCikis; mi++) {
    const cikisKatlar = gtMerdivenKatlar[String(mi)] || {};
    const hedefKatlar = [...tumKatlar].reverse().filter(kat => cikisKatlar[kat]);
    aydinlatmaCikislari.push({ tip: 'merdiven', idx: mi, cikisSlot: merdivenSlotStart + mi, katlar: hedefKatlar, katlarData: cikisKatlar });
  }
  for (let ai = 0; ai < gtAsansorCikis; ai++) {
    const cikisKatlar = gtAsansorKatlar[String(ai)] || {};
    const hedefKatlar = [...tumKatlar].reverse().filter(kat => cikisKatlar[kat]);
    aydinlatmaCikislari.push({ tip: 'asansor', idx: ai, cikisSlot: asansorSlotStart + ai, katlar: hedefKatlar, katlarData: cikisKatlar });
  }

  // Yukarı ve aşağı giden hatları ayır, ayrı stagger indexleri ata
  let yukariStagger = 0;
  let asagiStagger = 0;
  const cikisStaggerMap = []; // her çıkış için { yon: 'yukari'|'asagi', stagger }

  aydinlatmaCikislari.forEach(cikis => {
    if (!katYPositions || cikis.katlar.length === 0) {
      cikisStaggerMap.push(null);
      return;
    }
    // Hedef katların en üstünün mid Y'si enerji katından yukarı mı?
    const enUstKat = cikis.katlar[0];
    const enUstPos = katYPositions[enUstKat];
    const enUstMidY = enUstPos ? (enUstPos.top + enUstPos.bottom) / 2 : 0;

    if (enUstMidY > enerjiKatMidY) {
      cikisStaggerMap.push({ yon: 'yukari', stagger: yukariStagger });
      yukariStagger++;
    } else {
      cikisStaggerMap.push({ yon: 'asagi', stagger: asagiStagger });
      asagiStagger++;
    }
  });

  // Her çıkışı çiz
  aydinlatmaCikislari.forEach((cikis, ci) => {
    const staggerInfo = cikisStaggerMap[ci];
    if (!staggerInfo || !katYPositions || cikis.katlar.length === 0) return;

    const cikisY = gtCikisTopY - cikis.cikisSlot * 20;
    const sigortaEndX = gtSigortaEndXBase;
    const stIdx = staggerInfo.stagger;

    // 1. Sigortadan sağa: merdiven 345+jenOffset+stagger*10, asansör +100 ek
    const tipOffset = cikis.tip === 'asansor' ? 100 : 0;
    const yatayMesafe = 35 + taliSutunGenisligi + jeneratorSutunGenisligi + stIdx * 10 + tipOffset;
    objects.push(createLine(
      sigortaEndX, cikisY,
      sigortaEndX + yatayMesafe, cikisY,
      gtCikisColor,
      { role: `gt${cikis.tip}Yatay_${cikis.idx}` }
    ));

    // Yatay çizgi üzerine kablo bilgisi (sigorta sağında)
    const aydinlatmaKabloX = sigortaEndX + 117;
    objects.push(createTextSprite(
      '3x1.5 mm² NHXMH',
      aydinlatmaKabloX, cikisY + 4,
      10, fazHatCssColor
    ));

    // 2. Dikey ana hat
    const dikeyX = sigortaEndX + yatayMesafe;
    const enUstKat = cikis.katlar[0];
    const enUstKatPos = katYPositions[enUstKat];
    const enAltKat = cikis.katlar[cikis.katlar.length - 1];
    const enAltKatPos = katYPositions[enAltKat];
    if (!enUstKatPos) return;

    const enUstKatMidY = (enUstKatPos.top + enUstKatPos.bottom) / 2;
    const enAltKatMidY = enAltKatPos ? (enAltKatPos.top + enAltKatPos.bottom) / 2 : enUstKatMidY;

    const dikeyTopY = Math.max(enUstKatMidY, cikisY);
    const dikeyBottomY = Math.min(enAltKatMidY, cikisY);
    objects.push(createLine(
      dikeyX, dikeyBottomY,
      dikeyX, dikeyTopY,
      gtCikisColor,
      { role: `gt${cikis.tip}Dikey_${cikis.idx}` }
    ));

    // Yatay-dikey dönüş noktasında hem yukarı hem aşağı gidiş varsa buat koy
    const hatDonusY = cikisY; // yataydan dikeye dönüş noktası
    if (enUstKatMidY > hatDonusY && enAltKatMidY < hatDonusY) {
      objects.push(createConnectionDot(dikeyX, hatDonusY, gtCikisColor));
    }

    // 3. Her hedef kat için buat + yatay çizgi + lamba
    cikis.katlar.forEach((kat, ki) => {
      const katPos = katYPositions[kat];
      if (!katPos) return;
      const katMidY = (katPos.top + katPos.bottom) / 2;

      // Ek buat (bağlantı noktası) — dikey hat üzerinde (ilk ve son kat hariç, orası hat sonu)
      if (ki !== 0 && ki !== cikis.katlar.length - 1) {
        objects.push(createConnectionDot(dikeyX, katMidY, gtCikisColor));
      }

      // Sağa 65 - stagger*10 birim (lambalar aynı X'te hizalansın)
      const lambaYatay = 65 - stIdx * 10;
      objects.push(createLine(
        dikeyX, katMidY,
        dikeyX + lambaYatay, katMidY,
        gtCikisColor,
        { role: `gt${cikis.tip}LambaYatay_${cikis.idx}_${ki}` }
      ));

      // Lamba sembolü
      const lambaX = dikeyX + lambaYatay;
      const lambaEntities = Lamba(lambaX, katMidY, 0.5, 0, {});
      const lambaObjects = convertDxfToThreeJS(lambaEntities, gtCikisColor);
      objects.push(...lambaObjects);

      // Armatür sayısı ve gücü (lambanın üstünde)
      const katData = cikis.katlarData?.[kat];
      if (katData && typeof katData === 'object' && katData.count) {
        const perPower = katData.count > 0 ? Math.round(katData.power / katData.count) : 9;
        objects.push(createTextSprite(
          `${katData.count} x ${perPower}W`,
          lambaX, katMidY + 10,
          10, fazHatCssColor
        ));
      }
    });
  });

  // --- Zayıf akım hattı (sadece abone paneli varsa) ---
  if (zayifAkimVar) {
    const zayifAkimColor = 0x00ffff; // Cyan
    const zayifAkimCikisY = gtCikisTopY - zayifAkimSlotIdx * 20;
    const zayifAkimSigortaEndX = gtSigortaEndXBase;
    const panelsPerFloorZA = kolonConfig.panelsPerFloor || {};
    const hasPanelsPerFloorZA = kolonConfig.hasPanelData || Object.keys(panelsPerFloorZA).length > 0;

    // Asansör hattından 100 birim daha sağa (merdivenden 200 birim)
    const zayifAkimYatayMesafe = 235 + taliSutunGenisligi + jeneratorSutunGenisligi;
    objects.push(createLine(
      zayifAkimSigortaEndX, zayifAkimCikisY,
      zayifAkimSigortaEndX + zayifAkimYatayMesafe, zayifAkimCikisY,
      zayifAkimColor,
      { role: 'gtZayifAkimYatay' }
    ));

    // Yatay çizgi üzerine kablo bilgisi (sigorta sağında)
    const zaKabloX = zayifAkimSigortaEndX + 117;
    objects.push(createTextSprite(
      '2x0.75 mm² JY(St)Y',
      zaKabloX, zayifAkimCikisY + 4,
      8, fazHatCssColor
    ));

    // Dikey ana hat: tüm katları kapsar
    const zayifDikeyX = zayifAkimSigortaEndX + zayifAkimYatayMesafe;
    const zaEnUstKat = tumKatlar[tumKatlar.length - 1];
    const zaEnAltKat = tumKatlar[0];
    const zaEnUstPos = katYPositions?.[zaEnUstKat];
    const zaEnAltPos = katYPositions?.[zaEnAltKat];

    if (zaEnUstPos && zaEnAltPos) {
      const zaEnUstMidY = (zaEnUstPos.top + zaEnUstPos.bottom) / 2;
      const zaEnAltMidY = (zaEnAltPos.top + zaEnAltPos.bottom) / 2;
      const zaDikeyTopY = Math.max(zaEnUstMidY, zayifAkimCikisY);
      const zaDikeyBottomY = Math.min(zaEnAltMidY, zayifAkimCikisY);

      objects.push(createLine(
        zayifDikeyX, zaDikeyBottomY,
        zayifDikeyX, zaDikeyTopY,
        zayifAkimColor,
        { role: 'gtZayifAkimDikey' }
      ));

      // Yatay-dikey dönüşte iki yöne gidiyorsa buat
      if (zaEnUstMidY > zayifAkimCikisY && zaEnAltMidY < zayifAkimCikisY) {
        objects.push(createConnectionDot(zayifDikeyX, zayifAkimCikisY, zayifAkimColor));
      }

      // Her kat için: yatay çizgi + daire başına interkom
      tumKatlar.forEach((kat, katIdx) => {
        const katPos = katYPositions[kat];
        if (!katPos) return;
        const katMidY = (katPos.top + katPos.bottom) / 2;

        // Ek buat (ilk ve son kat hariç — hat sonu)
        if (katIdx !== 0 && katIdx !== tumKatlar.length - 1) {
          objects.push(createConnectionDot(zayifDikeyX, katMidY, zayifAkimColor));
        }

        // İnterkomlar arası 20 birim, ilk interkom 20 birim sağda
        const katDaireSayisi = hasPanelsPerFloorZA ? (panelsPerFloorZA[kat] || 0) : 1;
        const toplamInterkomGenislik = katDaireSayisi * 20;
        objects.push(createLine(
          zayifDikeyX, katMidY,
          zayifDikeyX + toplamInterkomGenislik, katMidY,
          zayifAkimColor,
          { role: `gtZayifAkimKatYatay_${katIdx}` }
        ));

        // Her daire için 20 birimlik dikey çizgi + interkom sembolü
        for (let di = 0; di < katDaireSayisi; di++) {
          const interkomX = zayifDikeyX + 20 + di * 20;

          // Ek buat — kesişim noktasında (son daire hariç, hat sonu)
          if (di !== katDaireSayisi - 1) {
            objects.push(createConnectionDot(interkomX, katMidY, zayifAkimColor));
          }

          // 10 birimlik dikey çizgi (yatay çizgiden yukarı, aşağı inmesin)
          objects.push(createLine(
            interkomX, katMidY,
            interkomX, katMidY + 10,
            zayifAkimColor,
            { role: `gtZayifAkimDik_${katIdx}_${di}` }
          ));

          // İnterkom sembolü (dikey çizginin üstünde)
          const interkomY = katMidY + 10 + 7; // sembol merkezi
          const interkomEntities = InterkonButonGrup(interkomX, interkomY, 0.4, 0, {});
          const interkomObjects = convertDxfToThreeJS(interkomEntities, zayifAkimColor);
          objects.push(...interkomObjects);
        }
      });
    }
  }

  // --- Tali Panel hatları ---
  const taliPanelColor = fazHatColor;

  // Kat bazında tali panel Y pozisyonlarını hesapla (kat alt hizasından yukarı doğru sıralama)
  const hesaplaTaliY = (taliList) => {
    // Kata göre grupla
    const katGruplari = {};
    taliList.forEach((tali, idx) => {
      if (!katGruplari[tali.kat]) katGruplari[tali.kat] = [];
      katGruplari[tali.kat].push(idx);
    });
    // Her panel için Y pozisyonu hesapla: kat alt sınırından yukarı doğru
    const yPozisyonlari = {};
    Object.entries(katGruplari).forEach(([kat, idxler]) => {
      const katPos = katYPositions?.[kat];
      if (!katPos) return;
      idxler.forEach((idx, sira) => {
        yPozisyonlari[idx] = katPos.bottom + 20 + sira * 25;
      });
    });
    return yPozisyonlari;
  };

  // Tali paneller için sabit X pozisyonları (GT AOS sağ kenarından 235 birim)
  const taliPanoLeftX = gtSigortaEndXBase + 235;        // panel sol kenarı
  const taliPanoCenterX = taliPanoLeftX + 20;           // PanoTali center (width=40)
  const taliAosCenterX = taliPanoLeftX - 32;            // AOS center (20 gap + 12 half-width)
  const altK = altTaliPaneller.length;

  // Transfer paneli bilgilerini topla (jeneratör bağlantısı için)
  const transferPanelBilgileri = [];

  // Üst tali panelleri çiz (GT'nin üst çıkışları)
  const ustTaliYPoz = hesaplaTaliY(ustTaliPaneller);
  ustTaliPaneller.forEach((tali, ti) => {
    const slotIdx = ti;
    const cikisY = gtCikisTopY - slotIdx * 20;
    const sigortaEndX = gtSigortaEndXBase;

    const panelY = ustTaliYPoz[ti];
    if (panelY === undefined) return;

    // 1. GT slottan dikeyX'e yatay çizgi (stagger korunur)
    const yatayMesafe = 45 + ti * 10;
    const dikeyX = sigortaEndX + yatayMesafe;
    objects.push(createLine(
      sigortaEndX, cikisY,
      dikeyX, cikisY,
      taliPanelColor,
      { role: `gtUstTaliYatay_${ti}` }
    ));

    const taliLabelX = taliPanoCenterX + 20 + 3; // panel sağ kenarı + 3 boşluk

    if (tali.isSiginak && jeneratorVar) {
      // === Sığınak + Jeneratör: GT → T.P. (üstten) → SP (alttan sağa) ===

      // T.P. pozisyonları
      const tpCenterX = taliPanoCenterX - 300;
      const tpY = panelY + 25;
      const tpTopY = tpY + 7;    // T.P. üst kenarı (giriş)
      const tpBottomY = tpY - 7; // T.P. alt kenarı (çıkış)
      const tpGirisY = tpTopY + 10;      // Yatay hat seviyesi (T.P. üstü + 10)
      const gtGirisX = tpCenterX + 10;   // GT giriş noktası (merkezin 10 sağında)
      const genGirisX = tpCenterX - 10;  // Jen giriş noktası (merkezin 10 solunda)

      // GT → T.P.: dikeyX'ten yatay seviyeye dikey
      objects.push(createLine(
        dikeyX, cikisY,
        dikeyX, tpGirisY,
        taliPanelColor,
        { role: `gtUstTaliDikey_${ti}` }
      ));

      // Kablo bilgisi (dikey ortada, 90°)
      const ustDikeyMidY = (cikisY + tpGirisY) / 2;
      const ustKabloSprite = createTextSprite(
        '3x2.5 mm² NYM-J',
        dikeyX - 2, ustDikeyMidY,
        8, fazHatCssColor
      );
      ustKabloSprite.material.rotation = (90 * Math.PI) / 180;
      objects.push(ustKabloSprite);

      // Yatay: dikeyX → GT giriş noktasına (tpGirisY seviyesinde)
      objects.push(createLine(
        dikeyX, tpGirisY,
        gtGirisX, tpGirisY,
        taliPanelColor,
        { role: `gtUstTaliTPgiris_yatay_${ti}` }
      ));

      // GT giriş noktasından dikey aşağı 10 birim → T.P. üst kenarına
      objects.push(createLine(
        gtGirisX, tpGirisY,
        gtGirisX, tpTopY,
        taliPanelColor,
        { role: `gtUstTaliTPgiris_dikey_${ti}` }
      ));

      // T.P. — 180° dönmüş
      const tpEntities = PanoTali(tpCenterX, tpY, 40, 14, 1, 180, {});
      const tpObjects = convertDxfToThreeJS(tpEntities, taliPanelColor);
      objects.push(...tpObjects);

      // T.P. yazısı (sağına, sola yaslı)
      const tpLabelX = tpCenterX + 20 + 3;
      objects.push(createTextSprite(
        'T.P.',
        tpLabelX, tpY + 7,
        8, fazHatCssColor, 'left'
      ));

      // T.P. altından çıkış → aşağı → sağa dönüp SP'ye
      objects.push(createLine(
        tpCenterX, tpBottomY,
        tpCenterX, panelY,
        taliPanelColor,
        { role: `gtUstTaliTPcikis_dikey_${ti}` }
      ));
      objects.push(createLine(
        tpCenterX, panelY,
        taliPanoLeftX, panelY,
        taliPanelColor,
        { role: `gtUstTaliTPcikis_yatay_${ti}` }
      ));

      // SP (sığınak paneli) her zamanki yerinde
      const taliPanoEntities = PanoTali(taliPanoCenterX, panelY, 40, 14, 1, 0, {});
      const taliPanoObjects = convertDxfToThreeJS(taliPanoEntities, taliPanelColor);
      objects.push(...taliPanoObjects);

      // SP yazıları (sola yaslı)
      objects.push(createTextSprite(
        tali.label,
        taliLabelX, panelY + 7,
        8, fazHatCssColor, 'left'
      ));
      objects.push(createTextSprite(
        `${Math.round(tali.power || 0)} W`,
        taliLabelX, panelY - 5,
        8, fazHatCssColor, 'left'
      ));

      // Transfer paneli bilgisini kaydet (jeneratör bağlantısı için)
      transferPanelBilgileri.push({ tpCenterX, tpY, siginakKat: tali.kat });
    } else {
      // === Normal tali panel: GT → AOS → Panel ===

      // 2. dikeyX'ten panelY'ye dikey çizgi
      objects.push(createLine(
        dikeyX, cikisY,
        dikeyX, panelY,
        taliPanelColor,
        { role: `gtUstTaliDikey_${ti}` }
      ));

      // 3. Kablo bilgisi (dikey ortada, 90°)
      const ustDikeyMidY = (cikisY + panelY) / 2;
      const ustKabloSprite = createTextSprite(
        '3x2.5 mm² NYM-J',
        dikeyX - 2, ustDikeyMidY,
        8, fazHatCssColor
      );
      ustKabloSprite.material.rotation = (90 * Math.PI) / 180;
      objects.push(ustKabloSprite);

      // 4. dikeyX'ten AOS sol kenarına yatay çizgi
      objects.push(createLine(
        dikeyX, panelY,
        taliAosCenterX - 12, panelY,
        taliPanelColor,
        { role: `gtUstTaliAosYatay_${ti}` }
      ));

      // 5. AOS sembolü
      const taliSigEntities = AnahtarliOtomatikSigorta(taliAosCenterX, panelY, 1, 1, 0, {}, true);
      const taliSigObjects = convertDxfToThreeJS(taliSigEntities, taliPanelColor);
      objects.push(...taliSigObjects);

      // AOS kesici değeri (üstünde)
      if (tali.breakerTitle) {
        objects.push(createTextSprite(
          tali.breakerTitle,
          taliAosCenterX, panelY + 10,
          7, fazHatCssColor
        ));
      }

      // 6. AOS sağ kenarından panel sol kenarına yatay çizgi
      objects.push(createLine(
        taliAosCenterX + 12, panelY,
        taliPanoLeftX, panelY,
        taliPanelColor,
        { role: `gtUstTaliPanoYatay_${ti}` }
      ));

      // 7. PanoTali
      const taliPanoEntities = PanoTali(taliPanoCenterX, panelY, 40, 14, 1, 0, {});
      const taliPanoObjects = convertDxfToThreeJS(taliPanoEntities, taliPanelColor);
      objects.push(...taliPanoObjects);

      // 8. Sağ tarafta: panel adı ve güç (sola yaslı)
      objects.push(createTextSprite(
        tali.label,
        taliLabelX, panelY + 7,
        8, fazHatCssColor, 'left'
      ));
      objects.push(createTextSprite(
        `${Math.round(tali.power || 0)} W`,
        taliLabelX, panelY - 5,
        8, fazHatCssColor, 'left'
      ));
    }
  });

  // Alt tali panelleri çiz (GT'nin alt çıkışları)
  const altTaliYPoz = hesaplaTaliY(altTaliPaneller);
  altTaliPaneller.forEach((tali, ti) => {
    const slotIdx = altTaliSlotStart + ti;
    const cikisY = gtCikisTopY - slotIdx * 20;
    const sigortaEndX = gtSigortaEndXBase;

    const panelY = altTaliYPoz[ti];
    if (panelY === undefined) return;

    // 1. GT slottan dikeyX'e yatay çizgi (stagger: ters sıra)
    const yatayMesafeAlt = 45 + (altK - ti - 1) * 10;
    const dikeyX = sigortaEndX + yatayMesafeAlt;
    objects.push(createLine(
      sigortaEndX, cikisY,
      dikeyX, cikisY,
      taliPanelColor,
      { role: `gtAltTaliYatay_${ti}` }
    ));

    const taliLabelX = taliPanoCenterX + 20 + 3;

    if (tali.isSiginak && jeneratorVar) {
      // === Sığınak + Jeneratör: GT → T.P. (üstten) → SP (alttan sağa) ===

      // T.P. pozisyonları
      const tpCenterX = taliPanoCenterX - 300;
      const tpY = panelY + 25;
      const tpTopY = tpY + 7;
      const tpBottomY = tpY - 7;
      const tpGirisY = tpTopY + 10;      // Yatay hat seviyesi (T.P. üstü + 10)
      const gtGirisX = tpCenterX + 10;   // GT giriş noktası (merkezin 10 sağında)
      const genGirisX = tpCenterX - 10;  // Jen giriş noktası (merkezin 10 solunda)

      // GT → T.P.: dikeyX'ten yatay seviyeye dikey
      objects.push(createLine(
        dikeyX, cikisY,
        dikeyX, tpGirisY,
        taliPanelColor,
        { role: `gtAltTaliDikey_${ti}` }
      ));

      // Kablo bilgisi (dikey ortada, 90°)
      const altDikeyMidY = (cikisY + tpGirisY) / 2;
      const altKabloSprite = createTextSprite(
        '3x2.5 mm² NYM-J',
        dikeyX - 2, altDikeyMidY,
        8, fazHatCssColor
      );
      altKabloSprite.material.rotation = (90 * Math.PI) / 180;
      objects.push(altKabloSprite);

      // Yatay: dikeyX → GT giriş noktasına (tpGirisY seviyesinde)
      objects.push(createLine(
        dikeyX, tpGirisY,
        gtGirisX, tpGirisY,
        taliPanelColor,
        { role: `gtAltTaliTPgiris_yatay_${ti}` }
      ));

      // GT giriş noktasından dikey aşağı 10 birim → T.P. üst kenarına
      objects.push(createLine(
        gtGirisX, tpGirisY,
        gtGirisX, tpTopY,
        taliPanelColor,
        { role: `gtAltTaliTPgiris_dikey_${ti}` }
      ));

      // T.P. — 180° dönmüş
      const tpEntities = PanoTali(tpCenterX, tpY, 40, 14, 1, 180, {});
      const tpObjects = convertDxfToThreeJS(tpEntities, taliPanelColor);
      objects.push(...tpObjects);

      // T.P. yazısı (sağına, sola yaslı)
      const tpLabelX = tpCenterX + 20 + 3;
      objects.push(createTextSprite(
        'T.P.',
        tpLabelX, tpY + 7,
        8, fazHatCssColor, 'left'
      ));

      // T.P. altından çıkış → aşağı → sağa dönüp SP'ye
      objects.push(createLine(
        tpCenterX, tpBottomY,
        tpCenterX, panelY,
        taliPanelColor,
        { role: `gtAltTaliTPcikis_dikey_${ti}` }
      ));
      objects.push(createLine(
        tpCenterX, panelY,
        taliPanoLeftX, panelY,
        taliPanelColor,
        { role: `gtAltTaliTPcikis_yatay_${ti}` }
      ));

      // SP (sığınak paneli) her zamanki yerinde
      const taliPanoEntities = PanoTali(taliPanoCenterX, panelY, 40, 14, 1, 0, {});
      const taliPanoObjects = convertDxfToThreeJS(taliPanoEntities, taliPanelColor);
      objects.push(...taliPanoObjects);

      // SP yazıları (sola yaslı)
      objects.push(createTextSprite(
        tali.label,
        taliLabelX, panelY + 7,
        8, fazHatCssColor, 'left'
      ));
      objects.push(createTextSprite(
        `${Math.round(tali.power || 0)} W`,
        taliLabelX, panelY - 5,
        8, fazHatCssColor, 'left'
      ));

      // Transfer paneli bilgisini kaydet (jeneratör bağlantısı için)
      transferPanelBilgileri.push({ tpCenterX, tpY, siginakKat: tali.kat });
    } else {
      // === Normal tali panel: GT → AOS → Panel ===

      // 2. dikeyX'ten panelY'ye dikey çizgi
      objects.push(createLine(
        dikeyX, cikisY,
        dikeyX, panelY,
        taliPanelColor,
        { role: `gtAltTaliDikey_${ti}` }
      ));

      // 3. Kablo bilgisi (dikey ortada, 90°)
      const altDikeyMidY = (cikisY + panelY) / 2;
      const altKabloSprite = createTextSprite(
        '3x2.5 mm² NYM-J',
        dikeyX - 2, altDikeyMidY,
        8, fazHatCssColor
      );
      altKabloSprite.material.rotation = (90 * Math.PI) / 180;
      objects.push(altKabloSprite);

      // 4. dikeyX'ten AOS sol kenarına yatay çizgi
      objects.push(createLine(
        dikeyX, panelY,
        taliAosCenterX - 12, panelY,
        taliPanelColor,
        { role: `gtAltTaliAosYatay_${ti}` }
      ));

      // 5. AOS sembolü
      const taliSigEntities = AnahtarliOtomatikSigorta(taliAosCenterX, panelY, 1, 1, 0, {}, true);
      const taliSigObjects = convertDxfToThreeJS(taliSigEntities, taliPanelColor);
      objects.push(...taliSigObjects);

      // AOS kesici değeri (üstünde)
      if (tali.breakerTitle) {
        objects.push(createTextSprite(
          tali.breakerTitle,
          taliAosCenterX, panelY + 10,
          7, fazHatCssColor
        ));
      }

      // 6. AOS sağ kenarından panel sol kenarına yatay çizgi
      objects.push(createLine(
        taliAosCenterX + 12, panelY,
        taliPanoLeftX, panelY,
        taliPanelColor,
        { role: `gtAltTaliPanoYatay_${ti}` }
      ));

      // 7. PanoTali
      const taliPanoEntities = PanoTali(taliPanoCenterX, panelY, 40, 14, 1, 0, {});
      const taliPanoObjects = convertDxfToThreeJS(taliPanoEntities, taliPanelColor);
      objects.push(...taliPanoObjects);

      // 8. Sağ tarafta: panel adı ve güç (sola yaslı)
      objects.push(createTextSprite(
        tali.label,
        taliLabelX, panelY + 7,
        8, fazHatCssColor, 'left'
      ));
      objects.push(createTextSprite(
        `${Math.round(tali.power || 0)} W`,
        taliLabelX, panelY - 5,
        8, fazHatCssColor, 'left'
      ));
    }
  });

  // --- Jeneratör çizimi ve transfer paneline bağlantı ---
  if (jeneratorVar && transferPanelBilgileri.length > 0) {
    transferPanelBilgileri.forEach(tp => {
      const { tpCenterX, tpY, siginakKat } = tp;
      const tpTopY = tpY + 7;
      const tpGirisY = tpTopY + 10;       // Yatay hat seviyesi (T.P. üstü + 10)
      const genGirisX = tpCenterX - 10;   // Jen giriş noktası (merkezin 10 solunda)

      // Jeneratör X: T.P.'nin 100 birim solunda
      const genX = tpCenterX - 100;
      // Jeneratör Y: T.P.'nin 80 birim üstünde (aynı kattayken)
      const sameFloorGenY = tpY + 80;

      // Sığınak katı bilgileri
      const siginakKatIdx = tumKatlar.indexOf(siginakKat);
      const siginakKatPos = katYPositions[siginakKat];
      if (!siginakKatPos) return;

      // Jeneratör kendi katında mı yoksa farklı bir katta mı?
      // Şimdilik sığınak paneli ile aynı kattaki pozisyonu kullan
      // Farklı katta ise: genY'yi o katın pozisyonuna taşı
      let genY = sameFloorGenY;

      // Jeneratör sembolü — Mak (kare + daire + yazı)
      const genEntities = Mak(genX, genY, 'G', 0.5, 0, {});
      const genObjects = convertDxfToThreeJS(genEntities, taliPanelColor);
      objects.push(...genObjects);

      // "Jeneratör" etiketi (sağına)
      objects.push(createTextSprite('Jeneratör', genX + 30, genY, 8, fazHatCssColor));

      // Bağlantı: jeneratör → T.P. üstünden giriş (yatay hat + dikey 10 birim)
      // 1. Jeneratörden dikey aşağı yatay hat seviyesine
      objects.push(createLine(
        genX, genY,
        genX, tpGirisY,
        taliPanelColor,
        { role: 'jeneratorDikey' }
      ));

      // 2. Yatay: genX → jen giriş noktasına (tpGirisY seviyesinde)
      objects.push(createLine(
        genX, tpGirisY,
        genGirisX, tpGirisY,
        taliPanelColor,
        { role: 'jeneratorYatay' }
      ));

      // 3. Jen giriş noktasından dikey aşağı 10 birim → T.P. üst kenarına
      objects.push(createLine(
        genGirisX, tpGirisY,
        genGirisX, tpTopY,
        taliPanelColor,
        { role: 'jeneratorGiris_dikey' }
      ));
    });
  }
  } // end if (kolonConfig.hasGT)

  return objects;
};

/**
 * Kat etiketleri oluştur
 */
const createFloorLabels = (katYPositions, energyRoomLeft, tumKatlar) => {
  const labels = [];
  const labelX = energyRoomLeft + 60; // Şemanın sol kenarından 60 birim içeride

  tumKatlar.forEach(kat => {
    const pos = katYPositions[kat];
    if (!pos) return;

    // Üst çizgiye 10 birim yakın
    const labelY = pos.top - 10;
    const displayName = kat.toUpperCase();

    labels.push(createTextSprite(
      displayName,
      labelX, labelY,
      14, '#ffffff'
    ));
  });

  return labels;
};

/**
 * Kolon şemasını temizle
 */
export const clearKolonSema = (scene) => {
  const toRemove = [];
  scene.traverse((obj) => {
    if (obj.userData?.isKolonSema) {
      toRemove.push(obj);
    }
  });

  toRemove.forEach((obj) => {
    if (obj.parent) {
      obj.parent.remove(obj);
    } else {
      scene.remove(obj);
    }
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) {
      if (Array.isArray(obj.material)) {
        obj.material.forEach(m => {
          if (m.map) m.map.dispose();
          m.dispose();
        });
      } else {
        if (obj.material.map) obj.material.map.dispose();
        obj.material.dispose();
      }
    }
  });
};

/**
 * Ana fonksiyon: Kolon şeması oluştur
 * @param {THREE.Scene} scene - Three.js sahne objesi
 * @param {Object} kolonConfig - TestPanel'den gelen yapılandırma
 * @param {Array} tumKatlar - Tüm katların sıralı listesi
 * @param {Object} options - Ek seçenekler
 */
export const generateKolonSemaMultiMeter = (scene, kolonConfig, tumKatlar, options = {}) => {
  if (!scene) {
    console.warn('[KolonSema] Scene objesi bulunamadı');
    return null;
  }

  // Merkez noktası (varsayılan: 0, 0)
  const centerX = options.centerX ?? 0;
  const centerY = options.centerY ?? 0;

  // Önce mevcut kolon şemasını temizle
  clearKolonSema(scene);

  // Toplam sayaç sayısını hesapla (GT sayacı sadece hasGT ise eklenir)
  // panelsPerFloor varsa gerçek panel dağılımını kullan, yoksa eski uniform hesap
  const panelsPerFloor = kolonConfig.panelsPerFloor || {};
  const katBasiDaire = parseInt(kolonConfig.katBasiDaire) || 0;
  const gtExtra = kolonConfig.hasGT ? 1 : 0;
  const totalMeterCount = Object.keys(panelsPerFloor).length > 0
    ? tumKatlar.reduce((sum, kat) => sum + (panelsPerFloor[kat] || 0), 0) + gtExtra
    : tumKatlar.length * katBasiDaire + gtExtra;

  // 0 panel durumu: hiç sayaç yok → çizim yapma
  if (totalMeterCount < 1) {
    console.warn('[KolonSema] Panel bulunamadı, kolon şeması oluşturulmadı');
    return null;
  }

  // Sonuç objesi
  const result = {
    centerMarker: null,
    floorLines: [],
    columnLines: [],
    bars: [],
    labels: [],
    bounds: null,
    katYPositions: null
  };

  // 1. Merkez noktası işaretçisi
  const centerMarker = createCenterMarker(centerX, centerY);
  scene.add(centerMarker);
  result.centerMarker = centerMarker;

  // 2. Baralar (R, S, T, Notr, Pe)
  const { objects: barObjects, barWidth } = createBars(centerX, centerY, totalMeterCount);
  barObjects.forEach(obj => scene.add(obj));
  result.bars = barObjects;

  // 3. Eş potansiyel bara (Pe barası bağlantısı)
  const esPotansiyelObjects = createEsPotansiyelBara(centerX, centerY, kolonConfig);
  esPotansiyelObjects.forEach(obj => scene.add(obj));

  // 4. Kat ve sütun çizgileri (katYPositions ana kolon hattında lazım)
  const config = {
    ...kolonConfig,
    tumKatlar: tumKatlar
  };

  const { lines, katYPositions, bounds } = createFloorLines(config, centerX, centerY);
  lines.forEach(line => scene.add(line));
  result.floorLines = lines;
  result.bounds = bounds;

  // 4b. Kat isimleri (sol tarafta, büyük harflerle)
  const floorLabels = createFloorLabels(katYPositions, bounds.left, tumKatlar);
  floorLabels.forEach(label => scene.add(label));
  result.katYPositions = katYPositions;

  // 5. Ana Kolon Hattı (kırmızı hat + TMS şalter)
  const anaKolonObjects = createAnaKolonHatti(centerX, centerY, kolonConfig, tumKatlar, katYPositions, barWidth);
  anaKolonObjects.forEach(obj => scene.add(obj));

  return result;
};

export default generateKolonSemaMultiMeter;
